/**
 * Year-level Foundations content shared by the WA and VIC year-level pages.
 *
 * Derived from the NSW year-level pages (src/pages/programs/Nsw*Foundations*.tsx)
 * with every NSW-only reference removed: NESA/Stage wording, OC, NSW Selective,
 * scholarship (not offered in WA) and the unverified "typically 25+" class-size
 * comparison. Tokens resolved per state in src/config/foundationsYearPages.ts:
 *   {STATE} {CURRICULUM} {YEAR0} {MINUTES} {PRICE} {BOOKS}
 * No prices are stored here - they come from course_pricing at runtime.
 */
export type IconName = string;

export type FoundationsYearContent = {
  subtitle: string;
  quickFacts: { icon: IconName; text: string }[];
  tabs: {
    id: string;
    icon: IconName;
    name: string;
    sub: string;
    items: { label: string; name: string; heading: string; para: string; bullets: string[] }[];
  }[];
  inclusions: { icon: IconName; name: string; desc: string; images?: string[] }[];
  faq: { q: string; a: string }[];
  siblingDesc: string;
  naplanRelated: boolean;
  seoDescription: string;
  schemaDescription: string;
};

/** Keyed by `${yearLevel}-${subject}`; yearLevel 0 is the first school year. */
export const FOUNDATIONS_YEAR_CONTENT: Record<string, FoundationsYearContent> = {
  "0-english": {
    "subtitle": "Build reading, writing and phonics in small groups - aligned to the {CURRICULUM} for English.",
    "tabs": [
      {
        "id": "term",
        "icon": "Calendar",
        "name": "Term course",
        "sub": "10 weekly sessions per term",
        "items": [
          {
            "label": "Overview",
            "name": "Course overview",
            "heading": "Term Course overview",
            "para": "The {YEAR0} English term course runs 10 sessions per term, {MINUTES} minutes each, in small groups of 8-12 students. Lessons are aligned to the {CURRICULUM} for English and designed to complement what students are learning at school.",
            "bullets": [
              "Reading, writing, phonics and oral language covered across the term",
              "Content sequenced alongside the school year so tutoring and classroom work reinforce each other",
              "Early literacy habits built through consistent, guided practice"
            ]
          },
          {
            "label": "Audience",
            "name": "Who it's for",
            "heading": "Who it's for",
            "para": "This course is ideal for students who…",
            "bullets": [
              "Want to develop strong early reading and writing habits",
              "Need extra support with phonics, decoding or sentence writing",
              "Are confident readers who want to be challenged further",
              "Benefit from a structured, small-group learning environment",
              "(Parents) want progress to be visible and reported each term"
            ]
          },
          {
            "label": "Benefits",
            "name": "What you'll gain",
            "heading": "What you'll gain",
            "para": "By the end of the term your child will have made measurable progress in:",
            "bullets": [
              "Reading - phonics, decoding, fluency and comprehension",
              "Writing - sentence construction, simple paragraphs and creative writing",
              "Grammar and punctuation - capital letters, full stops, sight words",
              "Speaking and listening - confidence to share ideas in a group"
            ]
          },
          {
            "label": "Content",
            "name": "What's covered",
            "heading": "What's covered",
            "para": "Topics are sequenced across the four school terms:",
            "bullets": [
              "Term 1 - Phonics and reading foundations: letter sounds, sight words, fluency",
              "Term 2 - Sentence writing: capital letters, full stops, simple paragraphs",
              "Term 3 - Creative writing: narrative structure, descriptive vocabulary",
              "Term 4 - Comprehension and consolidation: reading for meaning and review"
            ]
          },
          {
            "label": "Program",
            "name": "How it works",
            "heading": "How it works",
            "para": "Every session follows a consistent structure so students always know what to expect:",
            "bullets": [
              "Free level assessment before the first session - no wasted lessons on work already mastered",
              "Each {MINUTES}-min lesson: introduce the concept → model examples → guided practice → independent application",
              "Written and verbal feedback on every student's work each session",
              "End-of-term progress report so parents can see clearly what's improved"
            ]
          }
        ]
      },
      {
        "id": "holiday",
        "icon": "Sun",
        "name": "Holiday intensive",
        "sub": "4 daily lessons in the holidays",
        "items": [
          {
            "label": "Overview",
            "name": "Course overview",
            "heading": "Holiday intensive overview",
            "para": "The {YEAR0} English holiday intensive runs 4 daily sessions over the school holidays, {MINUTES} minutes each. The program focuses on one literacy skill intensively, allowing students to build confidence quickly.",
            "bullets": [
              "4 sessions, daily, {MINUTES} minutes each",
              "One skill - deep focus rather than broad coverage",
              "Same small groups and qualified educators as the term course",
              "No prior Schoolola enrolment needed"
            ]
          },
          {
            "label": "Audience",
            "name": "Who it's for",
            "heading": "Who it's for",
            "para": "The holiday intensive suits students who…",
            "bullets": [
              "Are building confidence in reading or writing",
              "Want a focused short boost before the next school term begins",
              "Can't commit to a full term but want structured, quality tuition",
              "Need to build confidence in one specific literacy skill quickly"
            ]
          },
          {
            "label": "Benefits",
            "name": "What you'll gain",
            "heading": "What you'll gain",
            "para": "After 4 sessions your child will have:",
            "bullets": [
              "A solid understanding of one literacy skill - structure, language, practice approach",
              "Strategies for tackling reading and writing tasks at school",
              "Increased confidence to participate in class literacy activities",
              "Specific written feedback on their own practice work"
            ]
          },
          {
            "label": "Content",
            "name": "What's covered",
            "heading": "What's covered",
            "para": "The four sessions follow a structured daily sequence:",
            "bullets": [
              "Day 1 - Skill overview: model texts, key features, simple framework",
              "Day 2 - Guided practice: scaffolded reading or writing tasks",
              "Day 3 - Independent application with educator support",
              "Day 4 - Showcase activity with full written feedback"
            ]
          },
          {
            "label": "Program",
            "name": "How it works",
            "heading": "How it works",
            "para": "The intensive is designed for maximum impact across four consecutive sessions:",
            "bullets": [
              "Register your interest - sessions fill quickly in school holiday periods",
              "Each day builds on the previous - daily attendance is expected",
              "Individual written feedback on Day 3 and Day 4 tasks included",
              "Students leave with a completed practice portfolio"
            ]
          }
        ]
      }
    ],
    "inclusions": [
      {
        "icon": "Award",
        "name": "Qualified educators",
        "desc": "Teachers with {CURRICULUM} expertise and classroom experience."
      },
      {
        "icon": "Users",
        "name": "Small group classes",
        "desc": "8-12 students per class. Your child gets seen, heard and responded to every lesson."
      },
      {
        "icon": "GraduationCap",
        "name": "{CURRICULUM} aligned",
        "desc": "Content mapped directly to {CURRICULUM} outcomes. Complements school, session by session."
      },
      {
        "icon": "MessageCircle",
        "name": "Feedback every lesson",
        "desc": "Structured written and verbal feedback on every student's work, every session."
      },
      {
        "icon": "FileText",
        "name": "End-of-term report",
        "desc": "A clear parent-facing progress summary at end of each term - before school reports arrive."
      },
      {
        "icon": "Target",
        "name": "Free assessment",
        "desc": "Level placement before the first session. No wasted lessons on work already mastered."
      },
      {
        "icon": "CheckCircle2",
        "name": "Strong foundations",
        "desc": "Early literacy habits built into the term sequence - a strong base for NAPLAN in later years."
      },
      {
        "icon": "Smartphone",
        "name": "Schoolola LMS",
        "desc": "Access theory lesson videos, online practice questions and track progress easily via the Schoolola LMS app.",
        "images": [
          "/lovable-uploads/lms-parent-dashboard.png",
          "/lovable-uploads/lms-learning-dashboard.png",
          "/lovable-uploads/lms-diagnostic-overview.png",
          "/lovable-uploads/lms-quiz-question-1.png",
          "/lovable-uploads/lms-quiz-question-2.png",
          "/lovable-uploads/lms-results-report.png"
        ]
      }
    ],
    "faq": [
      {
        "q": "Is {PRICE} the full price - are there any extras?",
        "a": "{PRICE} covers the session. Term enrolments include the end-of-term progress report at no extra cost. {BOOKS}"
      },
      {
        "q": "What study materials are provided?",
        "a": "Study materials are provided as bound books - no loose papers. {BOOKS}"
      },
      {
        "q": "How is this different from what happens at school?",
        "a": "Group sizes are smaller (8-12 students per class), lessons follow a consistent structured format, and every student receives written feedback on their own work each session. Content follows the same {CURRICULUM} outcomes - we're giving your child more practice and more attention within the same curriculum."
      },
      {
        "q": "Is this the right level for my {YEAR0} child?",
        "a": "The free assessment will confirm placement. If your child is in {YEAR0} and you want to strengthen early literacy or numeracy, this is the right starting point. The assessment happens before the first session."
      },
      {
        "q": "What if my child misses a session?",
        "a": "We ask for consistent attendance as each session builds on the last. If a session is missed, your educator can note the key material to follow up at home - but there are no standard make-up classes. Contact your local centre."
      },
      {
        "q": "Can a confident {YEAR0} student still be challenged?",
        "a": "Yes. The assessment places students within ability groups, not just year groups. A {YEAR0} student working above level will be placed in a group that challenges them accordingly."
      }
    ],
    "siblingDesc": "Number sense, counting and early problem solving",
    "naplanRelated": false,
    "seoDescription": "{YEAR0} English tutoring in {STATE}. {CURRICULUM} aligned phonics, reading, writing and early literacy. Small groups of 8-12 students.",
    "schemaDescription": "{YEAR0} English tuition in {STATE}. Small groups of 8-12 students, aligned to the {CURRICULUM}. Phonics, reading, writing and early literacy.",
    "quickFacts": [
      {
        "icon": "Clock",
        "text": "60 min / session"
      },
      {
        "icon": "Users",
        "text": "8-12 students"
      },
      {
        "icon": "MapPin",
        "text": "In-centre + online"
      },
      {
        "icon": "DollarSign",
        "text": "{PRICE} / session"
      }
    ]
  },
  "0-maths": {
    "subtitle": "Build number sense, counting and early problem solving in small groups - aligned to the {CURRICULUM} for Mathematics.",
    "tabs": [
      {
        "id": "term",
        "icon": "Calendar",
        "name": "Term course",
        "sub": "10 weekly sessions per term",
        "items": [
          {
            "label": "Overview",
            "name": "Course overview",
            "heading": "Term Course overview",
            "para": "The {YEAR0} Maths term course runs 10 sessions per term, {MINUTES} minutes each, in small groups of 8-12 students. Lessons are aligned to the {CURRICULUM} for Mathematics and designed to complement what students are learning at school.",
            "bullets": [
              "Number, measurement, geometry and early problem solving covered across the term",
              "Content sequenced alongside the school year so tutoring and classroom work reinforce each other",
              "Early numeracy habits built through consistent, hands-on practice"
            ]
          },
          {
            "label": "Audience",
            "name": "Who it's for",
            "heading": "Who it's for",
            "para": "This course is ideal for students who…",
            "bullets": [
              "Want to build a strong number sense from an early age",
              "Need extra support with counting, addition or place value",
              "Are confident with numbers and want to be extended further",
              "Benefit from a structured, small-group learning environment",
              "(Parents) want progress to be visible and reported each term"
            ]
          },
          {
            "label": "Benefits",
            "name": "What you'll gain",
            "heading": "What you'll gain",
            "para": "By the end of the term your child will have made measurable progress in:",
            "bullets": [
              "Number - counting, place value, addition and subtraction",
              "Measurement - length, mass, capacity and time",
              "Geometry - 2D shapes, 3D objects and position",
              "Problem solving - applying number skills to simple word problems"
            ]
          },
          {
            "label": "Content",
            "name": "What's covered",
            "heading": "What's covered",
            "para": "Topics are sequenced across the four school terms:",
            "bullets": [
              "Term 1 - Number sense: counting, comparing and ordering numbers",
              "Term 2 - Addition and subtraction strategies with concrete materials",
              "Term 3 - Measurement and geometry: shapes, length, time",
              "Term 4 - Problem solving and consolidation across all strands"
            ]
          },
          {
            "label": "Program",
            "name": "How it works",
            "heading": "How it works",
            "para": "Every session follows a consistent structure so students always know what to expect:",
            "bullets": [
              "Free level assessment before the first session - no wasted lessons on work already mastered",
              "Each {MINUTES}-min lesson: introduce the concept → model examples → guided practice → independent application",
              "Written and verbal feedback on every student's work each session",
              "End-of-term progress report so parents can see clearly what's improved"
            ]
          }
        ]
      },
      {
        "id": "holiday",
        "icon": "Sun",
        "name": "Holiday intensive",
        "sub": "4 daily lessons in the holidays",
        "items": [
          {
            "label": "Overview",
            "name": "Course overview",
            "heading": "Holiday intensive overview",
            "para": "The {YEAR0} Maths holiday intensive runs 4 daily sessions over the school holidays, {MINUTES} minutes each. The program focuses on one numeracy skill intensively, allowing students to build confidence quickly.",
            "bullets": [
              "4 sessions, daily, {MINUTES} minutes each",
              "One skill - deep focus rather than broad coverage",
              "Same small groups and qualified educators as the term course",
              "No prior Schoolola enrolment needed"
            ]
          },
          {
            "label": "Audience",
            "name": "Who it's for",
            "heading": "Who it's for",
            "para": "The holiday intensive suits students who…",
            "bullets": [
              "Are building confidence in number or problem solving",
              "Want a focused short boost before the next school term begins",
              "Can't commit to a full term but want structured, quality tuition",
              "Need to build confidence in one specific maths topic quickly"
            ]
          },
          {
            "label": "Benefits",
            "name": "What you'll gain",
            "heading": "What you'll gain",
            "para": "After 4 sessions your child will have:",
            "bullets": [
              "A solid understanding of one numeracy skill - concepts, strategies and practice",
              "Mental maths strategies for tackling problems at school",
              "Increased confidence to participate in class maths activities",
              "Specific written feedback on their own practice problems"
            ]
          },
          {
            "label": "Content",
            "name": "What's covered",
            "heading": "What's covered",
            "para": "The four sessions follow a structured daily sequence:",
            "bullets": [
              "Day 1 - Skill overview: key concepts, worked examples, mental strategies",
              "Day 2 - Guided practice: scaffolded problems with concrete materials",
              "Day 3 - Independent application with educator support",
              "Day 4 - Practice activity with full written feedback"
            ]
          },
          {
            "label": "Program",
            "name": "How it works",
            "heading": "How it works",
            "para": "The intensive is designed for maximum impact across four consecutive sessions:",
            "bullets": [
              "Register your interest - sessions fill quickly in school holiday periods",
              "Each day builds on the previous - daily attendance is expected",
              "Individual written feedback on Day 3 and Day 4 tasks included",
              "Students leave with a completed practice portfolio"
            ]
          }
        ]
      }
    ],
    "inclusions": [
      {
        "icon": "Award",
        "name": "Qualified educators",
        "desc": "Teachers with {CURRICULUM} expertise and classroom experience."
      },
      {
        "icon": "Users",
        "name": "Small group classes",
        "desc": "8-12 students per class. Your child gets seen, heard and responded to every lesson."
      },
      {
        "icon": "GraduationCap",
        "name": "{CURRICULUM} aligned",
        "desc": "Content mapped directly to {CURRICULUM} outcomes. Complements school, session by session."
      },
      {
        "icon": "MessageCircle",
        "name": "Feedback every lesson",
        "desc": "Structured written and verbal feedback on every student's work, every session."
      },
      {
        "icon": "FileText",
        "name": "End-of-term report",
        "desc": "A clear parent-facing progress summary at end of each term - before school reports arrive."
      },
      {
        "icon": "Target",
        "name": "Free assessment",
        "desc": "Level placement before the first session. No wasted lessons on work already mastered."
      },
      {
        "icon": "CheckCircle2",
        "name": "Strong foundations",
        "desc": "Early numeracy habits built into the term sequence - a strong base for NAPLAN in later years."
      },
      {
        "icon": "Smartphone",
        "name": "Schoolola LMS",
        "desc": "Access theory lesson videos, online practice questions and track progress easily via the Schoolola LMS app.",
        "images": [
          "/lovable-uploads/lms-parent-dashboard.png",
          "/lovable-uploads/lms-learning-dashboard.png",
          "/lovable-uploads/lms-diagnostic-overview.png",
          "/lovable-uploads/lms-quiz-question-1.png",
          "/lovable-uploads/lms-quiz-question-2.png",
          "/lovable-uploads/lms-results-report.png"
        ]
      }
    ],
    "faq": [
      {
        "q": "Is {PRICE} the full price - are there any extras?",
        "a": "{PRICE} covers the session. Term enrolments include the end-of-term progress report at no extra cost. {BOOKS}"
      },
      {
        "q": "What study materials are provided?",
        "a": "Study materials are provided as bound books - no loose papers. {BOOKS}"
      },
      {
        "q": "How is this different from what happens at school?",
        "a": "Group sizes are smaller (8-12 students per class), lessons follow a consistent structured format, and every student receives written feedback on their own work each session. Content follows the same {CURRICULUM} outcomes - we're giving your child more practice and more attention within the same curriculum."
      },
      {
        "q": "Is this the right level for my {YEAR0} child?",
        "a": "The free assessment will confirm placement. If your child is in {YEAR0} and you want to strengthen early literacy or numeracy, this is the right starting point. The assessment happens before the first session."
      },
      {
        "q": "What if my child misses a session?",
        "a": "We ask for consistent attendance as each session builds on the last. If a session is missed, your educator can note the key material to follow up at home - but there are no standard make-up classes. Contact your local centre."
      },
      {
        "q": "Can a confident {YEAR0} student still be challenged?",
        "a": "Yes. The assessment places students within ability groups, not just year groups. A {YEAR0} student working above level will be placed in a group that challenges them accordingly."
      }
    ],
    "siblingDesc": "Reading, writing and phonics",
    "naplanRelated": false,
    "seoDescription": "{YEAR0} Maths tutoring in {STATE}. {CURRICULUM} aligned number sense, counting and early problem solving. Small groups of 8-12 students.",
    "schemaDescription": "{YEAR0} Maths tuition in {STATE}. Small groups of 8-12 students, aligned to the {CURRICULUM}. Number sense, counting and early problem solving.",
    "quickFacts": [
      {
        "icon": "Clock",
        "text": "60 min / session"
      },
      {
        "icon": "Users",
        "text": "8-12 students"
      },
      {
        "icon": "MapPin",
        "text": "In-centre + online"
      },
      {
        "icon": "DollarSign",
        "text": "{PRICE} / session"
      }
    ]
  },
  "1-english": {
    "subtitle": "Build reading, writing and phonics in small groups - aligned to the {CURRICULUM} for English.",
    "tabs": [
      {
        "id": "term",
        "icon": "Calendar",
        "name": "Term course",
        "sub": "10 weekly sessions per term",
        "items": [
          {
            "label": "Overview",
            "name": "Course overview",
            "heading": "Term Course overview",
            "para": "The Year 1 English term course runs 10 sessions per term, {MINUTES} minutes each, in small groups of 8-12 students. Lessons are aligned to the {CURRICULUM} for English and designed to complement what students are learning at school.",
            "bullets": [
              "Reading, writing, phonics and oral language covered across the term",
              "Content sequenced alongside the school year so tutoring and classroom work reinforce each other",
              "Early literacy habits built through consistent, guided practice"
            ]
          },
          {
            "label": "Audience",
            "name": "Who it's for",
            "heading": "Who it's for",
            "para": "This course is ideal for students who…",
            "bullets": [
              "Want to develop strong early reading and writing habits",
              "Need extra support with phonics, decoding or sentence writing",
              "Are confident readers who want to be challenged further",
              "Benefit from a structured, small-group learning environment",
              "(Parents) want progress to be visible and reported each term"
            ]
          },
          {
            "label": "Benefits",
            "name": "What you'll gain",
            "heading": "What you'll gain",
            "para": "By the end of the term your child will have made measurable progress in:",
            "bullets": [
              "Reading - phonics, decoding, fluency and comprehension",
              "Writing - sentence construction, simple paragraphs and creative writing",
              "Grammar and punctuation - capital letters, full stops, sight words",
              "Speaking and listening - confidence to share ideas in a group"
            ]
          },
          {
            "label": "Content",
            "name": "What's covered",
            "heading": "What's covered",
            "para": "Topics are sequenced across the four school terms:",
            "bullets": [
              "Term 1 - Phonics and reading foundations: letter sounds, sight words, fluency",
              "Term 2 - Sentence writing: capital letters, full stops, simple paragraphs",
              "Term 3 - Creative writing: narrative structure, descriptive vocabulary",
              "Term 4 - Comprehension and consolidation: reading for meaning and review"
            ]
          },
          {
            "label": "Program",
            "name": "How it works",
            "heading": "How it works",
            "para": "Every session follows a consistent structure so students always know what to expect:",
            "bullets": [
              "Free level assessment before the first session - no wasted lessons on work already mastered",
              "Each {MINUTES}-min lesson: introduce the concept → model examples → guided practice → independent application",
              "Written and verbal feedback on every student's work each session",
              "End-of-term progress report so parents can see clearly what's improved"
            ]
          }
        ]
      },
      {
        "id": "holiday",
        "icon": "Sun",
        "name": "Holiday intensive",
        "sub": "4 daily lessons in the holidays",
        "items": [
          {
            "label": "Overview",
            "name": "Course overview",
            "heading": "Holiday intensive overview",
            "para": "The Year 1 English holiday intensive runs 4 daily sessions over the school holidays, {MINUTES} minutes each. The program focuses on one literacy skill intensively, allowing students to build confidence quickly.",
            "bullets": [
              "4 sessions, daily, {MINUTES} minutes each",
              "One skill - deep focus rather than broad coverage",
              "Same small groups and qualified educators as the term course",
              "No prior Schoolola enrolment needed"
            ]
          },
          {
            "label": "Audience",
            "name": "Who it's for",
            "heading": "Who it's for",
            "para": "The holiday intensive suits students who…",
            "bullets": [
              "Are building confidence in reading or writing",
              "Want a focused short boost before the next school term begins",
              "Can't commit to a full term but want structured, quality tuition",
              "Need to build confidence in one specific literacy skill quickly"
            ]
          },
          {
            "label": "Benefits",
            "name": "What you'll gain",
            "heading": "What you'll gain",
            "para": "After 4 sessions your child will have:",
            "bullets": [
              "A solid understanding of one literacy skill - structure, language, practice approach",
              "Strategies for tackling reading and writing tasks at school",
              "Increased confidence to participate in class literacy activities",
              "Specific written feedback on their own practice work"
            ]
          },
          {
            "label": "Content",
            "name": "What's covered",
            "heading": "What's covered",
            "para": "The four sessions follow a structured daily sequence:",
            "bullets": [
              "Day 1 - Skill overview: model texts, key features, simple framework",
              "Day 2 - Guided practice: scaffolded reading or writing tasks",
              "Day 3 - Independent application with educator support",
              "Day 4 - Showcase activity with full written feedback"
            ]
          },
          {
            "label": "Program",
            "name": "How it works",
            "heading": "How it works",
            "para": "The intensive is designed for maximum impact across four consecutive sessions:",
            "bullets": [
              "Register your interest - sessions fill quickly in school holiday periods",
              "Each day builds on the previous - daily attendance is expected",
              "Individual written feedback on Day 3 and Day 4 tasks included",
              "Students leave with a completed practice portfolio"
            ]
          }
        ]
      }
    ],
    "inclusions": [
      {
        "icon": "Award",
        "name": "Qualified educators",
        "desc": "Teachers with {CURRICULUM} expertise and classroom experience."
      },
      {
        "icon": "Users",
        "name": "Small group classes",
        "desc": "8-12 students per class. Your child gets seen, heard and responded to every lesson."
      },
      {
        "icon": "GraduationCap",
        "name": "{CURRICULUM} aligned",
        "desc": "Content mapped directly to {CURRICULUM} outcomes. Complements school, session by session."
      },
      {
        "icon": "MessageCircle",
        "name": "Feedback every lesson",
        "desc": "Structured written and verbal feedback on every student's work, every session."
      },
      {
        "icon": "FileText",
        "name": "End-of-term report",
        "desc": "A clear parent-facing progress summary at end of each term - before school reports arrive."
      },
      {
        "icon": "Target",
        "name": "Free assessment",
        "desc": "Level placement before the first session. No wasted lessons on work already mastered."
      },
      {
        "icon": "CheckCircle2",
        "name": "Strong foundations",
        "desc": "Early literacy habits built into the term sequence - a strong base for NAPLAN in later years."
      },
      {
        "icon": "Smartphone",
        "name": "Schoolola LMS",
        "desc": "Access theory lesson videos, online practice questions and track progress easily via the Schoolola LMS app.",
        "images": [
          "/lovable-uploads/lms-parent-dashboard.png",
          "/lovable-uploads/lms-learning-dashboard.png",
          "/lovable-uploads/lms-diagnostic-overview.png",
          "/lovable-uploads/lms-quiz-question-1.png",
          "/lovable-uploads/lms-quiz-question-2.png",
          "/lovable-uploads/lms-results-report.png"
        ]
      }
    ],
    "faq": [
      {
        "q": "Is {PRICE} the full price - are there any extras?",
        "a": "{PRICE} covers the session. Term enrolments include the end-of-term progress report at no extra cost. {BOOKS}"
      },
      {
        "q": "What study materials are provided?",
        "a": "Study materials are provided as bound books - no loose papers. {BOOKS}"
      },
      {
        "q": "How is this different from what happens at school?",
        "a": "Group sizes are smaller (8-12 students per class), lessons follow a consistent structured format, and every student receives written feedback on their own work each session. Content follows the same {CURRICULUM} outcomes - we're giving your child more practice and more attention within the same curriculum."
      },
      {
        "q": "Is this the right level for my Year 1 child?",
        "a": "The free assessment will confirm placement. If your child is in Year 1 and you want to strengthen early literacy or numeracy, this is the right starting point. The assessment happens before the first session."
      },
      {
        "q": "What if my child misses a session?",
        "a": "We ask for consistent attendance as each session builds on the last. If a session is missed, your educator can note the key material to follow up at home - but there are no standard make-up classes. Contact your local centre."
      },
      {
        "q": "Can a confident Year 1 student still be challenged?",
        "a": "Yes. The assessment places students within ability groups, not just year groups. A Year 1 student working above level will be placed in a group that challenges them accordingly."
      }
    ],
    "siblingDesc": "Number sense, counting and early problem solving",
    "naplanRelated": false,
    "seoDescription": "Year 1 English tutoring in {STATE}. {CURRICULUM} aligned phonics, reading, writing and early literacy. Small groups of 8-12 students.",
    "schemaDescription": "Year 1 English tuition in {STATE}. Small groups of 8-12 students, aligned to the {CURRICULUM}. Phonics, reading, writing and early literacy.",
    "quickFacts": [
      {
        "icon": "Clock",
        "text": "60 min / session"
      },
      {
        "icon": "Users",
        "text": "8-12 students"
      },
      {
        "icon": "MapPin",
        "text": "In-centre + online"
      },
      {
        "icon": "DollarSign",
        "text": "{PRICE} / session"
      }
    ]
  },
  "1-maths": {
    "subtitle": "Build number sense, counting and early problem solving in small groups - aligned to the {CURRICULUM} for Mathematics.",
    "tabs": [
      {
        "id": "term",
        "icon": "Calendar",
        "name": "Term course",
        "sub": "10 weekly sessions per term",
        "items": [
          {
            "label": "Overview",
            "name": "Course overview",
            "heading": "Term Course overview",
            "para": "The Year 1 Maths term course runs 10 sessions per term, {MINUTES} minutes each, in small groups of 8-12 students. Lessons are aligned to the {CURRICULUM} for Mathematics and designed to complement what students are learning at school.",
            "bullets": [
              "Number, measurement, geometry and early problem solving covered across the term",
              "Content sequenced alongside the school year so tutoring and classroom work reinforce each other",
              "Early numeracy habits built through consistent, hands-on practice"
            ]
          },
          {
            "label": "Audience",
            "name": "Who it's for",
            "heading": "Who it's for",
            "para": "This course is ideal for students who…",
            "bullets": [
              "Want to build a strong number sense from an early age",
              "Need extra support with counting, addition or place value",
              "Are confident with numbers and want to be extended further",
              "Benefit from a structured, small-group learning environment",
              "(Parents) want progress to be visible and reported each term"
            ]
          },
          {
            "label": "Benefits",
            "name": "What you'll gain",
            "heading": "What you'll gain",
            "para": "By the end of the term your child will have made measurable progress in:",
            "bullets": [
              "Number - counting, place value, addition and subtraction",
              "Measurement - length, mass, capacity and time",
              "Geometry - 2D shapes, 3D objects and position",
              "Problem solving - applying number skills to simple word problems"
            ]
          },
          {
            "label": "Content",
            "name": "What's covered",
            "heading": "What's covered",
            "para": "Topics are sequenced across the four school terms:",
            "bullets": [
              "Term 1 - Number sense: counting, comparing and ordering numbers",
              "Term 2 - Addition and subtraction strategies with concrete materials",
              "Term 3 - Measurement and geometry: shapes, length, time",
              "Term 4 - Problem solving and consolidation across all strands"
            ]
          },
          {
            "label": "Program",
            "name": "How it works",
            "heading": "How it works",
            "para": "Every session follows a consistent structure so students always know what to expect:",
            "bullets": [
              "Free level assessment before the first session - no wasted lessons on work already mastered",
              "Each {MINUTES}-min lesson: introduce the concept → model examples → guided practice → independent application",
              "Written and verbal feedback on every student's work each session",
              "End-of-term progress report so parents can see clearly what's improved"
            ]
          }
        ]
      },
      {
        "id": "holiday",
        "icon": "Sun",
        "name": "Holiday intensive",
        "sub": "4 daily lessons in the holidays",
        "items": [
          {
            "label": "Overview",
            "name": "Course overview",
            "heading": "Holiday intensive overview",
            "para": "The Year 1 Maths holiday intensive runs 4 daily sessions over the school holidays, {MINUTES} minutes each. The program focuses on one numeracy skill intensively, allowing students to build confidence quickly.",
            "bullets": [
              "4 sessions, daily, {MINUTES} minutes each",
              "One skill - deep focus rather than broad coverage",
              "Same small groups and qualified educators as the term course",
              "No prior Schoolola enrolment needed"
            ]
          },
          {
            "label": "Audience",
            "name": "Who it's for",
            "heading": "Who it's for",
            "para": "The holiday intensive suits students who…",
            "bullets": [
              "Are building confidence in number or problem solving",
              "Want a focused short boost before the next school term begins",
              "Can't commit to a full term but want structured, quality tuition",
              "Need to build confidence in one specific maths topic quickly"
            ]
          },
          {
            "label": "Benefits",
            "name": "What you'll gain",
            "heading": "What you'll gain",
            "para": "After 4 sessions your child will have:",
            "bullets": [
              "A solid understanding of one numeracy skill - concepts, strategies and practice",
              "Mental maths strategies for tackling problems at school",
              "Increased confidence to participate in class maths activities",
              "Specific written feedback on their own practice problems"
            ]
          },
          {
            "label": "Content",
            "name": "What's covered",
            "heading": "What's covered",
            "para": "The four sessions follow a structured daily sequence:",
            "bullets": [
              "Day 1 - Skill overview: key concepts, worked examples, mental strategies",
              "Day 2 - Guided practice: scaffolded problems with concrete materials",
              "Day 3 - Independent application with educator support",
              "Day 4 - Practice activity with full written feedback"
            ]
          },
          {
            "label": "Program",
            "name": "How it works",
            "heading": "How it works",
            "para": "The intensive is designed for maximum impact across four consecutive sessions:",
            "bullets": [
              "Register your interest - sessions fill quickly in school holiday periods",
              "Each day builds on the previous - daily attendance is expected",
              "Individual written feedback on Day 3 and Day 4 tasks included",
              "Students leave with a completed practice portfolio"
            ]
          }
        ]
      }
    ],
    "inclusions": [
      {
        "icon": "Award",
        "name": "Qualified educators",
        "desc": "Teachers with {CURRICULUM} expertise and classroom experience."
      },
      {
        "icon": "Users",
        "name": "Small group classes",
        "desc": "8-12 students per class. Your child gets seen, heard and responded to every lesson."
      },
      {
        "icon": "GraduationCap",
        "name": "{CURRICULUM} aligned",
        "desc": "Content mapped directly to {CURRICULUM} outcomes. Complements school, session by session."
      },
      {
        "icon": "MessageCircle",
        "name": "Feedback every lesson",
        "desc": "Structured written and verbal feedback on every student's work, every session."
      },
      {
        "icon": "FileText",
        "name": "End-of-term report",
        "desc": "A clear parent-facing progress summary at end of each term - before school reports arrive."
      },
      {
        "icon": "Target",
        "name": "Free assessment",
        "desc": "Level placement before the first session. No wasted lessons on work already mastered."
      },
      {
        "icon": "CheckCircle2",
        "name": "Strong foundations",
        "desc": "Early numeracy habits built into the term sequence - a strong base for NAPLAN in later years."
      },
      {
        "icon": "Smartphone",
        "name": "Schoolola LMS",
        "desc": "Access theory lesson videos, online practice questions and track progress easily via the Schoolola LMS app.",
        "images": [
          "/lovable-uploads/lms-parent-dashboard.png",
          "/lovable-uploads/lms-learning-dashboard.png",
          "/lovable-uploads/lms-diagnostic-overview.png",
          "/lovable-uploads/lms-quiz-question-1.png",
          "/lovable-uploads/lms-quiz-question-2.png",
          "/lovable-uploads/lms-results-report.png"
        ]
      }
    ],
    "faq": [
      {
        "q": "Is {PRICE} the full price - are there any extras?",
        "a": "{PRICE} covers the session. Term enrolments include the end-of-term progress report at no extra cost. {BOOKS}"
      },
      {
        "q": "What study materials are provided?",
        "a": "Study materials are provided as bound books - no loose papers. {BOOKS}"
      },
      {
        "q": "How is this different from what happens at school?",
        "a": "Group sizes are smaller (8-12 students per class), lessons follow a consistent structured format, and every student receives written feedback on their own work each session. Content follows the same {CURRICULUM} outcomes - we're giving your child more practice and more attention within the same curriculum."
      },
      {
        "q": "Is this the right level for my Year 1 child?",
        "a": "The free assessment will confirm placement. If your child is in Year 1 and you want to strengthen early literacy or numeracy, this is the right starting point. The assessment happens before the first session."
      },
      {
        "q": "What if my child misses a session?",
        "a": "We ask for consistent attendance as each session builds on the last. If a session is missed, your educator can note the key material to follow up at home - but there are no standard make-up classes. Contact your local centre."
      },
      {
        "q": "Can a confident Year 1 student still be challenged?",
        "a": "Yes. The assessment places students within ability groups, not just year groups. A Year 1 student working above level will be placed in a group that challenges them accordingly."
      }
    ],
    "siblingDesc": "Reading, writing and phonics",
    "naplanRelated": false,
    "seoDescription": "Year 1 Maths tutoring in {STATE}. {CURRICULUM} aligned number sense, counting and early problem solving. Small groups of 8-12 students.",
    "schemaDescription": "Year 1 Maths tuition in {STATE}. Small groups of 8-12 students, aligned to the {CURRICULUM}. Number sense, counting and early problem solving.",
    "quickFacts": [
      {
        "icon": "Clock",
        "text": "60 min / session"
      },
      {
        "icon": "Users",
        "text": "8-12 students"
      },
      {
        "icon": "MapPin",
        "text": "In-centre + online"
      },
      {
        "icon": "DollarSign",
        "text": "{PRICE} / session"
      }
    ]
  },
  "2-english": {
    "subtitle": "Build reading, writing and phonics in small groups - aligned to the {CURRICULUM} for English.",
    "tabs": [
      {
        "id": "term",
        "icon": "Calendar",
        "name": "Term course",
        "sub": "10 weekly sessions per term",
        "items": [
          {
            "label": "Overview",
            "name": "Course overview",
            "heading": "Term Course overview",
            "para": "The Year 2 English term course runs 10 sessions per term, {MINUTES} minutes each, in small groups of 8-12 students. Lessons are aligned to the {CURRICULUM} for English and designed to complement what students are learning at school.",
            "bullets": [
              "Reading, writing, phonics and oral language covered across the term",
              "Content sequenced alongside the school year so tutoring and classroom work reinforce each other",
              "Early literacy habits built through consistent, guided practice"
            ]
          },
          {
            "label": "Audience",
            "name": "Who it's for",
            "heading": "Who it's for",
            "para": "This course is ideal for students who…",
            "bullets": [
              "Want to develop strong early reading and writing habits",
              "Need extra support with phonics, decoding or sentence writing",
              "Are confident readers who want to be challenged further",
              "Benefit from a structured, small-group learning environment",
              "(Parents) want progress to be visible and reported each term"
            ]
          },
          {
            "label": "Benefits",
            "name": "What you'll gain",
            "heading": "What you'll gain",
            "para": "By the end of the term your child will have made measurable progress in:",
            "bullets": [
              "Reading - phonics, decoding, fluency and comprehension",
              "Writing - sentence construction, simple paragraphs and creative writing",
              "Grammar and punctuation - capital letters, full stops, sight words",
              "Speaking and listening - confidence to share ideas in a group"
            ]
          },
          {
            "label": "Content",
            "name": "What's covered",
            "heading": "What's covered",
            "para": "Topics are sequenced across the four school terms:",
            "bullets": [
              "Term 1 - Phonics and reading foundations: letter sounds, sight words, fluency",
              "Term 2 - Sentence writing: capital letters, full stops, simple paragraphs",
              "Term 3 - Creative writing: narrative structure, descriptive vocabulary",
              "Term 4 - Comprehension and consolidation: reading for meaning and review"
            ]
          },
          {
            "label": "Program",
            "name": "How it works",
            "heading": "How it works",
            "para": "Every session follows a consistent structure so students always know what to expect:",
            "bullets": [
              "Free level assessment before the first session - no wasted lessons on work already mastered",
              "Each {MINUTES}-min lesson: introduce the concept → model examples → guided practice → independent application",
              "Written and verbal feedback on every student's work each session",
              "End-of-term progress report so parents can see clearly what's improved"
            ]
          }
        ]
      },
      {
        "id": "holiday",
        "icon": "Sun",
        "name": "Holiday intensive",
        "sub": "4 daily lessons in the holidays",
        "items": [
          {
            "label": "Overview",
            "name": "Course overview",
            "heading": "Holiday intensive overview",
            "para": "The Year 2 English holiday intensive runs 4 daily sessions over the school holidays, {MINUTES} minutes each. The program focuses on one literacy skill intensively, allowing students to build confidence quickly.",
            "bullets": [
              "4 sessions, daily, {MINUTES} minutes each",
              "One skill - deep focus rather than broad coverage",
              "Same small groups and qualified educators as the term course",
              "No prior Schoolola enrolment needed"
            ]
          },
          {
            "label": "Audience",
            "name": "Who it's for",
            "heading": "Who it's for",
            "para": "The holiday intensive suits students who…",
            "bullets": [
              "Are building confidence in reading or writing",
              "Want a focused short boost before the next school term begins",
              "Can't commit to a full term but want structured, quality tuition",
              "Need to build confidence in one specific literacy skill quickly"
            ]
          },
          {
            "label": "Benefits",
            "name": "What you'll gain",
            "heading": "What you'll gain",
            "para": "After 4 sessions your child will have:",
            "bullets": [
              "A solid understanding of one literacy skill - structure, language, practice approach",
              "Strategies for tackling reading and writing tasks at school",
              "Increased confidence to participate in class literacy activities",
              "Specific written feedback on their own practice work"
            ]
          },
          {
            "label": "Content",
            "name": "What's covered",
            "heading": "What's covered",
            "para": "The four sessions follow a structured daily sequence:",
            "bullets": [
              "Day 1 - Skill overview: model texts, key features, simple framework",
              "Day 2 - Guided practice: scaffolded reading or writing tasks",
              "Day 3 - Independent application with educator support",
              "Day 4 - Showcase activity with full written feedback"
            ]
          },
          {
            "label": "Program",
            "name": "How it works",
            "heading": "How it works",
            "para": "The intensive is designed for maximum impact across four consecutive sessions:",
            "bullets": [
              "Register your interest - sessions fill quickly in school holiday periods",
              "Each day builds on the previous - daily attendance is expected",
              "Individual written feedback on Day 3 and Day 4 tasks included",
              "Students leave with a completed practice portfolio"
            ]
          }
        ]
      }
    ],
    "inclusions": [
      {
        "icon": "Award",
        "name": "Qualified educators",
        "desc": "Teachers with {CURRICULUM} expertise and classroom experience."
      },
      {
        "icon": "Users",
        "name": "Small group classes",
        "desc": "8-12 students per class. Your child gets seen, heard and responded to every lesson."
      },
      {
        "icon": "GraduationCap",
        "name": "{CURRICULUM} aligned",
        "desc": "Content mapped directly to {CURRICULUM} outcomes. Complements school, session by session."
      },
      {
        "icon": "MessageCircle",
        "name": "Feedback every lesson",
        "desc": "Structured written and verbal feedback on every student's work, every session."
      },
      {
        "icon": "FileText",
        "name": "End-of-term report",
        "desc": "A clear parent-facing progress summary at end of each term - before school reports arrive."
      },
      {
        "icon": "Target",
        "name": "Free assessment",
        "desc": "Level placement before the first session. No wasted lessons on work already mastered."
      },
      {
        "icon": "CheckCircle2",
        "name": "Strong foundations",
        "desc": "Early literacy habits built into the term sequence - a strong base for NAPLAN in later years."
      },
      {
        "icon": "Smartphone",
        "name": "Schoolola LMS",
        "desc": "Access theory lesson videos, online practice questions and track progress easily via the Schoolola LMS app.",
        "images": [
          "/lovable-uploads/lms-parent-dashboard.png",
          "/lovable-uploads/lms-learning-dashboard.png",
          "/lovable-uploads/lms-diagnostic-overview.png",
          "/lovable-uploads/lms-quiz-question-1.png",
          "/lovable-uploads/lms-quiz-question-2.png",
          "/lovable-uploads/lms-results-report.png"
        ]
      }
    ],
    "faq": [
      {
        "q": "Is {PRICE} the full price - are there any extras?",
        "a": "{PRICE} covers the session. Term enrolments include the end-of-term progress report at no extra cost. {BOOKS}"
      },
      {
        "q": "What study materials are provided?",
        "a": "Study materials are provided as bound books - no loose papers. {BOOKS}"
      },
      {
        "q": "How is this different from what happens at school?",
        "a": "Group sizes are smaller (8-12 students per class), lessons follow a consistent structured format, and every student receives written feedback on their own work each session. Content follows the same {CURRICULUM} outcomes - we're giving your child more practice and more attention within the same curriculum."
      },
      {
        "q": "Is this the right level for my Year 2 child?",
        "a": "The free assessment will confirm placement. If your child is in Year 2 and you want to strengthen early literacy or numeracy, this is the right starting point. The assessment happens before the first session."
      },
      {
        "q": "What if my child misses a session?",
        "a": "We ask for consistent attendance as each session builds on the last. If a session is missed, your educator can note the key material to follow up at home - but there are no standard make-up classes. Contact your local centre."
      },
      {
        "q": "Can a confident Year 2 student still be challenged?",
        "a": "Yes. The assessment places students within ability groups, not just year groups. A Year 2 student working above level will be placed in a group that challenges them accordingly."
      }
    ],
    "siblingDesc": "Number sense, counting and early problem solving",
    "naplanRelated": false,
    "seoDescription": "Year 2 English tutoring in {STATE}. {CURRICULUM} aligned phonics, reading, writing and early literacy. Small groups of 8-12 students.",
    "schemaDescription": "Year 2 English tuition in {STATE}. Small groups of 8-12 students, aligned to the {CURRICULUM}. Phonics, reading, writing and early literacy.",
    "quickFacts": [
      {
        "icon": "Clock",
        "text": "60 min / session"
      },
      {
        "icon": "Users",
        "text": "8-12 students"
      },
      {
        "icon": "MapPin",
        "text": "In-centre + online"
      },
      {
        "icon": "DollarSign",
        "text": "{PRICE} / session"
      }
    ]
  },
  "2-maths": {
    "subtitle": "Build number sense, counting and early problem solving in small groups - aligned to the {CURRICULUM} for Mathematics.",
    "tabs": [
      {
        "id": "term",
        "icon": "Calendar",
        "name": "Term course",
        "sub": "10 weekly sessions per term",
        "items": [
          {
            "label": "Overview",
            "name": "Course overview",
            "heading": "Term Course overview",
            "para": "The Year 2 Maths term course runs 10 sessions per term, {MINUTES} minutes each, in small groups of 8-12 students. Lessons are aligned to the {CURRICULUM} for Mathematics and designed to complement what students are learning at school.",
            "bullets": [
              "Number, measurement, geometry and early problem solving covered across the term",
              "Content sequenced alongside the school year so tutoring and classroom work reinforce each other",
              "Early numeracy habits built through consistent, hands-on practice"
            ]
          },
          {
            "label": "Audience",
            "name": "Who it's for",
            "heading": "Who it's for",
            "para": "This course is ideal for students who…",
            "bullets": [
              "Want to build a strong number sense from an early age",
              "Need extra support with counting, addition or place value",
              "Are confident with numbers and want to be extended further",
              "Benefit from a structured, small-group learning environment",
              "(Parents) want progress to be visible and reported each term"
            ]
          },
          {
            "label": "Benefits",
            "name": "What you'll gain",
            "heading": "What you'll gain",
            "para": "By the end of the term your child will have made measurable progress in:",
            "bullets": [
              "Number - counting, place value, addition and subtraction",
              "Measurement - length, mass, capacity and time",
              "Geometry - 2D shapes, 3D objects and position",
              "Problem solving - applying number skills to simple word problems"
            ]
          },
          {
            "label": "Content",
            "name": "What's covered",
            "heading": "What's covered",
            "para": "Topics are sequenced across the four school terms:",
            "bullets": [
              "Term 1 - Number sense: counting, comparing and ordering numbers",
              "Term 2 - Addition and subtraction strategies with concrete materials",
              "Term 3 - Measurement and geometry: shapes, length, time",
              "Term 4 - Problem solving and consolidation across all strands"
            ]
          },
          {
            "label": "Program",
            "name": "How it works",
            "heading": "How it works",
            "para": "Every session follows a consistent structure so students always know what to expect:",
            "bullets": [
              "Free level assessment before the first session - no wasted lessons on work already mastered",
              "Each {MINUTES}-min lesson: introduce the concept → model examples → guided practice → independent application",
              "Written and verbal feedback on every student's work each session",
              "End-of-term progress report so parents can see clearly what's improved"
            ]
          }
        ]
      },
      {
        "id": "holiday",
        "icon": "Sun",
        "name": "Holiday intensive",
        "sub": "4 daily lessons in the holidays",
        "items": [
          {
            "label": "Overview",
            "name": "Course overview",
            "heading": "Holiday intensive overview",
            "para": "The Year 2 Maths holiday intensive runs 4 daily sessions over the school holidays, {MINUTES} minutes each. The program focuses on one numeracy skill intensively, allowing students to build confidence quickly.",
            "bullets": [
              "4 sessions, daily, {MINUTES} minutes each",
              "One skill - deep focus rather than broad coverage",
              "Same small groups and qualified educators as the term course",
              "No prior Schoolola enrolment needed"
            ]
          },
          {
            "label": "Audience",
            "name": "Who it's for",
            "heading": "Who it's for",
            "para": "The holiday intensive suits students who…",
            "bullets": [
              "Are building confidence in number or problem solving",
              "Want a focused short boost before the next school term begins",
              "Can't commit to a full term but want structured, quality tuition",
              "Need to build confidence in one specific maths topic quickly"
            ]
          },
          {
            "label": "Benefits",
            "name": "What you'll gain",
            "heading": "What you'll gain",
            "para": "After 4 sessions your child will have:",
            "bullets": [
              "A solid understanding of one numeracy skill - concepts, strategies and practice",
              "Mental maths strategies for tackling problems at school",
              "Increased confidence to participate in class maths activities",
              "Specific written feedback on their own practice problems"
            ]
          },
          {
            "label": "Content",
            "name": "What's covered",
            "heading": "What's covered",
            "para": "The four sessions follow a structured daily sequence:",
            "bullets": [
              "Day 1 - Skill overview: key concepts, worked examples, mental strategies",
              "Day 2 - Guided practice: scaffolded problems with concrete materials",
              "Day 3 - Independent application with educator support",
              "Day 4 - Practice activity with full written feedback"
            ]
          },
          {
            "label": "Program",
            "name": "How it works",
            "heading": "How it works",
            "para": "The intensive is designed for maximum impact across four consecutive sessions:",
            "bullets": [
              "Register your interest - sessions fill quickly in school holiday periods",
              "Each day builds on the previous - daily attendance is expected",
              "Individual written feedback on Day 3 and Day 4 tasks included",
              "Students leave with a completed practice portfolio"
            ]
          }
        ]
      }
    ],
    "inclusions": [
      {
        "icon": "Award",
        "name": "Qualified educators",
        "desc": "Teachers with {CURRICULUM} expertise and classroom experience."
      },
      {
        "icon": "Users",
        "name": "Small group classes",
        "desc": "8-12 students per class. Your child gets seen, heard and responded to every lesson."
      },
      {
        "icon": "GraduationCap",
        "name": "{CURRICULUM} aligned",
        "desc": "Content mapped directly to {CURRICULUM} outcomes. Complements school, session by session."
      },
      {
        "icon": "MessageCircle",
        "name": "Feedback every lesson",
        "desc": "Structured written and verbal feedback on every student's work, every session."
      },
      {
        "icon": "FileText",
        "name": "End-of-term report",
        "desc": "A clear parent-facing progress summary at end of each term - before school reports arrive."
      },
      {
        "icon": "Target",
        "name": "Free assessment",
        "desc": "Level placement before the first session. No wasted lessons on work already mastered."
      },
      {
        "icon": "CheckCircle2",
        "name": "Strong foundations",
        "desc": "Early numeracy habits built into the term sequence - a strong base for NAPLAN in later years."
      },
      {
        "icon": "Smartphone",
        "name": "Schoolola LMS",
        "desc": "Access theory lesson videos, online practice questions and track progress easily via the Schoolola LMS app.",
        "images": [
          "/lovable-uploads/lms-parent-dashboard.png",
          "/lovable-uploads/lms-learning-dashboard.png",
          "/lovable-uploads/lms-diagnostic-overview.png",
          "/lovable-uploads/lms-quiz-question-1.png",
          "/lovable-uploads/lms-quiz-question-2.png",
          "/lovable-uploads/lms-results-report.png"
        ]
      }
    ],
    "faq": [
      {
        "q": "Is {PRICE} the full price - are there any extras?",
        "a": "{PRICE} covers the session. Term enrolments include the end-of-term progress report at no extra cost. {BOOKS}"
      },
      {
        "q": "What study materials are provided?",
        "a": "Study materials are provided as bound books - no loose papers. {BOOKS}"
      },
      {
        "q": "How is this different from what happens at school?",
        "a": "Group sizes are smaller (8-12 students per class), lessons follow a consistent structured format, and every student receives written feedback on their own work each session. Content follows the same {CURRICULUM} outcomes - we're giving your child more practice and more attention within the same curriculum."
      },
      {
        "q": "Is this the right level for my Year 2 child?",
        "a": "The free assessment will confirm placement. If your child is in Year 2 and you want to strengthen early literacy or numeracy, this is the right starting point. The assessment happens before the first session."
      },
      {
        "q": "What if my child misses a session?",
        "a": "We ask for consistent attendance as each session builds on the last. If a session is missed, your educator can note the key material to follow up at home - but there are no standard make-up classes. Contact your local centre."
      },
      {
        "q": "Can a confident Year 2 student still be challenged?",
        "a": "Yes. The assessment places students within ability groups, not just year groups. A Year 2 student working above level will be placed in a group that challenges them accordingly."
      }
    ],
    "siblingDesc": "Reading, writing and phonics",
    "naplanRelated": false,
    "seoDescription": "Year 2 Maths tutoring in {STATE}. {CURRICULUM} aligned number sense, counting and early problem solving. Small groups of 8-12 students.",
    "schemaDescription": "Year 2 Maths tuition in {STATE}. Small groups of 8-12 students, aligned to the {CURRICULUM}. Number sense, counting and early problem solving.",
    "quickFacts": [
      {
        "icon": "Clock",
        "text": "60 min / session"
      },
      {
        "icon": "Users",
        "text": "8-12 students"
      },
      {
        "icon": "MapPin",
        "text": "In-centre + online"
      },
      {
        "icon": "DollarSign",
        "text": "{PRICE} / session"
      }
    ]
  },
  "3-english": {
    "subtitle": "Build reading, writing and grammar foundations in small groups - aligned to the {CURRICULUM}.",
    "tabs": [
      {
        "id": "term",
        "icon": "Calendar",
        "name": "Term course",
        "sub": "10 weekly sessions per term",
        "items": [
          {
            "label": "Overview",
            "name": "Course overview",
            "heading": "Term Course overview",
            "para": "The Year 3 English term course runs 10 sessions per term, {MINUTES} minutes each, in small groups of 8-12 students. Lessons are aligned to the {CURRICULUM} for English and designed to complement what students are learning at school.",
            "bullets": [
              "Reading comprehension, writing and grammar covered across the term",
              "Content sequenced alongside the school year so tutoring and classroom work reinforce each other",
              "Year 3 is a NAPLAN test year - preparation is woven into the program"
            ]
          },
          {
            "label": "Audience",
            "name": "Who it's for",
            "heading": "Who it's for",
            "para": "This course is ideal for students who…",
            "bullets": [
              "Want to get ahead of upcoming school content early",
              "Aim for stronger performance in class tasks and assessments",
              "Prefer a clear structured approach and consistent written feedback",
              "Are building writing confidence through regular guided practice",
              "(Parents) want progress to be visible and reported each term"
            ]
          },
          {
            "label": "Benefits",
            "name": "What you'll gain",
            "heading": "What you'll gain",
            "para": "By the end of the term your child will have made measurable progress in:",
            "bullets": [
              "Reading comprehension - literal and inferential meaning, text structure, vocabulary",
              "Writing - narrative, informative and persuasive texts with structured feedback on each",
              "Grammar and punctuation - sentence construction, homophones, paragraph organisation",
              "NAPLAN readiness - language conventions and writing under timed conditions"
            ]
          },
          {
            "label": "Content",
            "name": "What's covered",
            "heading": "What's covered",
            "para": "Topics are sequenced across the four school terms:",
            "bullets": [
              "Term 1 - Reading foundations: text types, comprehension strategies, vocabulary",
              "Term 2 - Writing: narrative structure, character, settings and descriptive language",
              "Term 3 - Persuasive writing and NAPLAN preparation",
              "Term 4 - Language conventions: grammar, punctuation, spelling, extended writing"
            ]
          },
          {
            "label": "Program",
            "name": "How it works",
            "heading": "How it works",
            "para": "Every session follows a consistent structure so students always know what to expect:",
            "bullets": [
              "Free level assessment before the first session - no wasted lessons on work already mastered",
              "Each {MINUTES}-min lesson: introduce the concept → model examples → guided practice → independent application",
              "Written and verbal feedback on every student's work each session",
              "End-of-term progress report so parents can see clearly what's improved"
            ]
          }
        ]
      },
      {
        "id": "holiday",
        "icon": "Sun",
        "name": "Holiday intensive",
        "sub": "4 daily lessons in the holidays",
        "items": [
          {
            "label": "Overview",
            "name": "Course overview",
            "heading": "Holiday intensive overview",
            "para": "The Year 3 English holiday intensive runs 4 daily sessions over the school holidays, {MINUTES} minutes each. The program focuses on one writing genre intensively, allowing students to build skill and confidence quickly.",
            "bullets": [
              "4 sessions, daily, {MINUTES} minutes each",
              "One writing genre - deep focus rather than broad coverage",
              "Same small groups and qualified educators as the term course",
              "No prior Schoolola enrolment needed"
            ]
          },
          {
            "label": "Audience",
            "name": "Who it's for",
            "heading": "Who it's for",
            "para": "The holiday intensive suits students who…",
            "bullets": [
              "Are preparing for upcoming NAPLAN writing and want targeted practice",
              "Want a focused short boost before the next school term begins",
              "Can't commit to a full term but want structured, quality tuition",
              "Need to build confidence in one specific writing type quickly"
            ]
          },
          {
            "label": "Benefits",
            "name": "What you'll gain",
            "heading": "What you'll gain",
            "para": "After 4 sessions your child will have:",
            "bullets": [
              "A solid understanding of one writing genre - structure, language features, planning approach",
              "NAPLAN writing strategies: how to approach the task under timed conditions",
              "Increased confidence to tackle writing tasks at school",
              "Specific written feedback on their own practice writing"
            ]
          },
          {
            "label": "Content",
            "name": "What's covered",
            "heading": "What's covered",
            "para": "The four sessions follow a structured daily sequence:",
            "bullets": [
              "Day 1 - Genre overview: model texts, key features, planning framework",
              "Day 2 - Planning and drafting: scaffold practice, vocabulary development",
              "Day 3 - Revision and editing: sentence variety, language choices, checking against criteria",
              "Day 4 - Practice task under assessment conditions with full written feedback"
            ]
          },
          {
            "label": "Program",
            "name": "How it works",
            "heading": "How it works",
            "para": "The intensive is designed for maximum impact across four consecutive sessions:",
            "bullets": [
              "Register your interest - sessions fill quickly in school holiday periods",
              "Each day builds on the previous - daily attendance is expected",
              "Individual written feedback on Day 3 and Day 4 tasks included",
              "Students leave with a completed writing portfolio piece"
            ]
          }
        ]
      }
    ],
    "inclusions": [
      {
        "icon": "Award",
        "name": "Qualified educators",
        "desc": "Teachers with {CURRICULUM} expertise and classroom experience."
      },
      {
        "icon": "Users",
        "name": "Small group classes",
        "desc": "8-12 students per class. Your child gets seen, heard and responded to every lesson."
      },
      {
        "icon": "GraduationCap",
        "name": "{CURRICULUM} aligned",
        "desc": "Content mapped directly to {CURRICULUM} outcomes. Complements school, session by session."
      },
      {
        "icon": "MessageCircle",
        "name": "Feedback every lesson",
        "desc": "Structured written and verbal feedback on every student's work, every session."
      },
      {
        "icon": "FileText",
        "name": "End-of-term report",
        "desc": "A clear parent-facing progress summary at end of each term - before school reports arrive."
      },
      {
        "icon": "Target",
        "name": "Free assessment",
        "desc": "Level placement before the first session. No wasted lessons on work already mastered."
      },
      {
        "icon": "CheckCircle2",
        "name": "NAPLAN readiness",
        "desc": "Year 3 NAPLAN preparation is built into the term sequence - not a separate add-on."
      },
      {
        "icon": "Smartphone",
        "name": "Schoolola LMS",
        "desc": "Access theory lesson videos, online practice questions and track progress easily via the Schoolola LMS app.",
        "images": [
          "/lovable-uploads/lms-parent-dashboard.png",
          "/lovable-uploads/lms-learning-dashboard.png",
          "/lovable-uploads/lms-diagnostic-overview.png",
          "/lovable-uploads/lms-quiz-question-1.png",
          "/lovable-uploads/lms-quiz-question-2.png",
          "/lovable-uploads/lms-results-report.png"
        ]
      }
    ],
    "faq": [
      {
        "q": "Is {PRICE} the full price - are there any extras?",
        "a": "{PRICE} covers the session. Term enrolments include the end-of-term progress report at no extra cost. {BOOKS}"
      },
      {
        "q": "What study materials are provided?",
        "a": "Study materials are provided as bound books - no loose papers. {BOOKS}"
      },
      {
        "q": "How is this different from what happens at school?",
        "a": "Group sizes are smaller (8-12 students per class), lessons follow a consistent structured format, and every student receives written feedback on their own work each session. Content follows the same {CURRICULUM} outcomes - we're giving your child more practice and more attention within the same curriculum."
      },
      {
        "q": "Is this the right level for my Year 3 child?",
        "a": "The free assessment will confirm placement. If your child is in Year 3 and you want to strengthen reading, writing or grammar - or prepare for NAPLAN - this is the right starting point. The assessment happens before the first session."
      },
      {
        "q": "What if my child misses a session?",
        "a": "We ask for consistent attendance as each session builds on the last. If a session is missed, your educator can note the key material to follow up at home - but there are no standard make-up classes. Contact your local centre."
      },
      {
        "q": "Can a confident Year 3 student still be challenged?",
        "a": "Yes. The assessment places students within ability groups, not just year groups. A Year 3 student working above level will be placed in a group that challenges them accordingly."
      }
    ],
    "siblingDesc": "Number, geometry and problem solving",
    "naplanRelated": true,
    "seoDescription": "Year 3 English tutoring in {STATE}. {CURRICULUM} aligned reading, writing, grammar and NAPLAN preparation. Small groups of 8-12 students.",
    "schemaDescription": "Year 3 English tuition in {STATE}. Small groups of 8-12 students, aligned to the {CURRICULUM}. Reading, writing, grammar and NAPLAN preparation.",
    "quickFacts": [
      {
        "icon": "Clock",
        "text": "60 min / session"
      },
      {
        "icon": "Users",
        "text": "8-12 students"
      },
      {
        "icon": "MapPin",
        "text": "In-centre + online"
      },
      {
        "icon": "DollarSign",
        "text": "{PRICE} / session"
      }
    ]
  },
  "3-maths": {
    "subtitle": "Build number sense, problem-solving and reasoning skills in small groups - aligned to the {CURRICULUM} for Mathematics.",
    "tabs": [
      {
        "id": "term",
        "icon": "Calendar",
        "name": "Term course",
        "sub": "10 weekly sessions per term",
        "items": [
          {
            "label": "Overview",
            "name": "Course overview",
            "heading": "Term Course overview",
            "para": "The Year 3 Maths term course runs 10 sessions per term, {MINUTES} minutes each, in small groups of 8-12 students. Lessons are aligned to the {CURRICULUM} for Mathematics and designed to complement what students are learning at school.",
            "bullets": [
              "Number and algebra, measurement and geometry, and statistics and probability covered across the term",
              "Content sequenced alongside the school year so tutoring and classroom work reinforce each other",
              "Year 3 is a NAPLAN test year - numeracy preparation is woven into the program"
            ]
          },
          {
            "label": "Audience",
            "name": "Who it's for",
            "heading": "Who it's for",
            "para": "This course is ideal for students who…",
            "bullets": [
              "Want to get ahead of upcoming school maths content early",
              "Aim for stronger performance in class tasks and assessments",
              "Prefer a clear structured approach with consistent practice and feedback",
              "Are building problem-solving confidence through regular guided practice",
              "(Parents) want progress to be visible and reported each term"
            ]
          },
          {
            "label": "Benefits",
            "name": "What you'll gain",
            "heading": "What you'll gain",
            "para": "By the end of the term your child will have made measurable progress in:",
            "bullets": [
              "Number sense - place value, addition, subtraction, multiplication and division",
              "Fractions - recognising, comparing and using halves, quarters and eighths",
              "Measurement and geometry - length, area, volume, time, 2D and 3D shapes",
              "NAPLAN numeracy readiness - multi-step problem solving under timed conditions"
            ]
          },
          {
            "label": "Content",
            "name": "What's covered",
            "heading": "What's covered",
            "para": "Topics are sequenced across the four school terms:",
            "bullets": [
              "Term 1 - Number foundations: place value to 1000, addition and subtraction strategies",
              "Term 2 - Multiplication and division facts, introduction to fractions",
              "Term 3 - Measurement, geometry and NAPLAN numeracy preparation",
              "Term 4 - Data and probability, time, and consolidation of key strategies"
            ]
          },
          {
            "label": "Program",
            "name": "How it works",
            "heading": "How it works",
            "para": "Every session follows a consistent structure so students always know what to expect:",
            "bullets": [
              "Free level assessment before the first session - no wasted lessons on work already mastered",
              "Each {MINUTES}-min lesson: introduce the concept → worked examples → guided practice → independent problem solving",
              "Written and verbal feedback on every student's work each session",
              "End-of-term progress report so parents can see clearly what's improved"
            ]
          }
        ]
      },
      {
        "id": "holiday",
        "icon": "Sun",
        "name": "Holiday intensive",
        "sub": "4 daily lessons in the holidays",
        "items": [
          {
            "label": "Overview",
            "name": "Course overview",
            "heading": "Holiday intensive overview",
            "para": "The Year 3 Maths holiday intensive runs 4 daily sessions over the school holidays, {MINUTES} minutes each. The program focuses on one key strand intensively, allowing students to build skill and confidence quickly.",
            "bullets": [
              "4 sessions, daily, {MINUTES} minutes each",
              "One maths strand - deep focus rather than broad coverage",
              "Same small groups and qualified educators as the term course",
              "No prior Schoolola enrolment needed"
            ]
          },
          {
            "label": "Audience",
            "name": "Who it's for",
            "heading": "Who it's for",
            "para": "The holiday intensive suits students who…",
            "bullets": [
              "Are preparing for upcoming NAPLAN numeracy and want targeted practice",
              "Want a focused short boost before the next school term begins",
              "Can't commit to a full term but want structured, quality tuition",
              "Need to build confidence in one specific maths topic quickly"
            ]
          },
          {
            "label": "Benefits",
            "name": "What you'll gain",
            "heading": "What you'll gain",
            "para": "After 4 sessions your child will have:",
            "bullets": [
              "A solid understanding of one strand - concepts, strategies, and worked examples",
              "NAPLAN numeracy strategies: how to approach multi-step problems under timed conditions",
              "Increased confidence to tackle maths tasks at school",
              "Specific written feedback on their own practice problems"
            ]
          },
          {
            "label": "Content",
            "name": "What's covered",
            "heading": "What's covered",
            "para": "The four sessions follow a structured daily sequence:",
            "bullets": [
              "Day 1 - Strand overview: key concepts, worked examples, mental strategies",
              "Day 2 - Guided practice: scaffold problems, vocabulary and notation",
              "Day 3 - Problem solving: multi-step questions, reasoning and checking",
              "Day 4 - Practice task under assessment conditions with full written feedback"
            ]
          },
          {
            "label": "Program",
            "name": "How it works",
            "heading": "How it works",
            "para": "The intensive is designed for maximum impact across four consecutive sessions:",
            "bullets": [
              "Register your interest - sessions fill quickly in school holiday periods",
              "Each day builds on the previous - daily attendance is expected",
              "Individual written feedback on Day 3 and Day 4 tasks included",
              "Students leave with a completed problem-solving portfolio"
            ]
          }
        ]
      }
    ],
    "inclusions": [
      {
        "icon": "Award",
        "name": "Qualified educators",
        "desc": "Teachers with {CURRICULUM} expertise and classroom experience."
      },
      {
        "icon": "Users",
        "name": "Small group classes",
        "desc": "8-12 students per class. Your child gets seen, heard and responded to every lesson."
      },
      {
        "icon": "GraduationCap",
        "name": "{CURRICULUM} aligned",
        "desc": "Content mapped directly to {CURRICULUM} outcomes. Complements school, session by session."
      },
      {
        "icon": "MessageCircle",
        "name": "Feedback every lesson",
        "desc": "Structured written and verbal feedback on every student's work, every session."
      },
      {
        "icon": "FileText",
        "name": "End-of-term report",
        "desc": "A clear parent-facing progress summary at end of each term - before school reports arrive."
      },
      {
        "icon": "Target",
        "name": "Free assessment",
        "desc": "Level placement before the first session. No wasted lessons on work already mastered."
      },
      {
        "icon": "CheckCircle2",
        "name": "NAPLAN readiness",
        "desc": "Year 3 NAPLAN numeracy preparation is built into the term sequence - not a separate add-on."
      },
      {
        "icon": "Smartphone",
        "name": "Schoolola LMS",
        "desc": "Access theory lesson videos, online practice questions and track progress easily via the Schoolola LMS app.",
        "images": [
          "/lovable-uploads/lms-parent-dashboard.png",
          "/lovable-uploads/lms-learning-dashboard.png",
          "/lovable-uploads/lms-diagnostic-overview.png",
          "/lovable-uploads/lms-quiz-question-1.png",
          "/lovable-uploads/lms-quiz-question-2.png",
          "/lovable-uploads/lms-results-report.png"
        ]
      }
    ],
    "faq": [
      {
        "q": "Is {PRICE} the full price - are there any extras?",
        "a": "{PRICE} covers the session. Term enrolments include the end-of-term progress report at no extra cost. {BOOKS}"
      },
      {
        "q": "What study materials are provided?",
        "a": "Study materials are provided as bound books - no loose papers. {BOOKS}"
      },
      {
        "q": "How is this different from what happens at school?",
        "a": "Group sizes are smaller (8-12 students per class), lessons follow a consistent structured format, and every student receives written feedback on their own work each session. Content follows the same {CURRICULUM} outcomes - we're giving your child more practice and more attention within the same curriculum."
      },
      {
        "q": "Is this the right level for my Year 3 child?",
        "a": "The free assessment will confirm placement. If your child is in Year 3 and you want to strengthen number, problem solving or NAPLAN numeracy - this is the right starting point. The assessment happens before the first session."
      },
      {
        "q": "What if my child misses a session?",
        "a": "We ask for consistent attendance as each session builds on the last. If a session is missed, your educator can note the key material to follow up at home - but there are no standard make-up classes. Contact your local centre."
      },
      {
        "q": "Can a confident Year 3 student still be challenged?",
        "a": "Yes. The assessment places students within ability groups, not just year groups. A Year 3 student working above level will be placed in a group that challenges them accordingly."
      }
    ],
    "siblingDesc": "Reading, writing and grammar foundations",
    "naplanRelated": true,
    "seoDescription": "Year 3 Maths tutoring in {STATE}. {CURRICULUM} aligned number, geometry, problem solving and NAPLAN numeracy preparation. Small groups of 8-12 students.",
    "schemaDescription": "Year 3 Maths tuition in {STATE}. Small groups of 8-12 students, aligned to the {CURRICULUM}. Number, geometry, problem solving and NAPLAN numeracy preparation.",
    "quickFacts": [
      {
        "icon": "Clock",
        "text": "60 min / session"
      },
      {
        "icon": "Users",
        "text": "8-12 students"
      },
      {
        "icon": "MapPin",
        "text": "In-centre + online"
      },
      {
        "icon": "DollarSign",
        "text": "{PRICE} / session"
      }
    ]
  },
  "4-english": {
    "subtitle": "Strengthen reading, writing and grammar in small groups - aligned to the {CURRICULUM} for English and a strong springboard into Year 5.",
    "tabs": [
      {
        "id": "term",
        "icon": "Calendar",
        "name": "Term course",
        "sub": "10 weekly sessions per term",
        "items": [
          {
            "label": "Overview",
            "name": "Course overview",
            "heading": "Term Course overview",
            "para": "The Year 4 English term course runs 10 sessions per term, {MINUTES} minutes each, in small groups of 8-12 students. Lessons are aligned to the {CURRICULUM} for English and consolidate the foundations needed for Year 5 and beyond.",
            "bullets": [
              "Reading comprehension, writing and grammar covered across the term",
              "Content sequenced alongside the school year so tutoring and classroom work reinforce each other",
              "Strong preparation for Year 5 NAPLAN"
            ]
          },
          {
            "label": "Audience",
            "name": "Who it's for",
            "heading": "Who it's for",
            "para": "This course is ideal for students who…",
            "bullets": [
              "Want to get ahead of upcoming school content early",
              "Aim for stronger performance in class tasks and assessments",
              "Are building writing confidence through regular guided practice",
              "(Parents) want progress to be visible and reported each term"
            ]
          },
          {
            "label": "Benefits",
            "name": "What you'll gain",
            "heading": "What you'll gain",
            "para": "By the end of the term your child will have made measurable progress in:",
            "bullets": [
              "Reading comprehension - inferential reasoning, text structure, sophisticated vocabulary",
              "Writing - narrative, informative and persuasive texts with structured feedback on each",
              "Grammar and punctuation - complex sentences, paragraphing, editing skills"
            ]
          },
          {
            "label": "Content",
            "name": "What's covered",
            "heading": "What's covered",
            "para": "Topics are sequenced across the four school terms:",
            "bullets": [
              "Term 1 - Reading: inferential comprehension, vocabulary, text analysis",
              "Term 2 - Writing: narrative craft, character development, descriptive language",
              "Term 3 - Persuasive writing and Year 5 NAPLAN preparation",
              "Term 4 - Language conventions: grammar, punctuation, spelling, extended writing"
            ]
          },
          {
            "label": "Program",
            "name": "How it works",
            "heading": "How it works",
            "para": "Every session follows a consistent structure so students always know what to expect:",
            "bullets": [
              "Free level assessment before the first session - no wasted lessons on work already mastered",
              "Each {MINUTES}-min lesson: introduce the concept → model examples → guided practice → independent application",
              "Written and verbal feedback on every student's work each session",
              "End-of-term progress report so parents can see clearly what's improved"
            ]
          }
        ]
      },
      {
        "id": "holiday",
        "icon": "Sun",
        "name": "Holiday intensive",
        "sub": "4 daily lessons in the holidays",
        "items": [
          {
            "label": "Overview",
            "name": "Course overview",
            "heading": "Holiday intensive overview",
            "para": "The Year 4 English holiday intensive runs 4 daily sessions over the school holidays, {MINUTES} minutes each. The program focuses on one writing genre intensively, allowing students to build skill and confidence quickly.",
            "bullets": [
              "4 sessions, daily, {MINUTES} minutes each",
              "One writing genre - deep focus rather than broad coverage",
              "Same small groups and qualified educators as the term course",
              "No prior Schoolola enrolment needed"
            ]
          },
          {
            "label": "Audience",
            "name": "Who it's for",
            "heading": "Who it's for",
            "para": "The holiday intensive suits students who…",
            "bullets": [
              "Are preparing for want targeted writing practice",
              "Want a focused short boost before the next school term begins",
              "Can't commit to a full term but want structured, quality tuition",
              "Need to build confidence in one specific writing type quickly"
            ]
          },
          {
            "label": "Benefits",
            "name": "What you'll gain",
            "heading": "What you'll gain",
            "para": "After 4 sessions your child will have:",
            "bullets": [
              "A solid understanding of one writing genre - structure, language features, planning approach",
              "Writing strategies for NAPLAN under timed conditions",
              "Increased confidence to tackle writing tasks at school",
              "Specific written feedback on their own practice writing"
            ]
          },
          {
            "label": "Content",
            "name": "What's covered",
            "heading": "What's covered",
            "para": "The four sessions follow a structured daily sequence:",
            "bullets": [
              "Day 1 - Genre overview: model texts, key features, planning framework",
              "Day 2 - Planning and drafting: scaffold practice, vocabulary development",
              "Day 3 - Revision and editing: sentence variety, language choices, checking against criteria",
              "Day 4 - Practice task under assessment conditions with full written feedback"
            ]
          },
          {
            "label": "Program",
            "name": "How it works",
            "heading": "How it works",
            "para": "The intensive is designed for maximum impact across four consecutive sessions:",
            "bullets": [
              "Register your interest - sessions fill quickly in school holiday periods",
              "Each day builds on the previous - daily attendance is expected",
              "Individual written feedback on Day 3 and Day 4 tasks included",
              "Students leave with a completed writing portfolio piece"
            ]
          }
        ]
      }
    ],
    "inclusions": [
      {
        "icon": "Award",
        "name": "Qualified educators",
        "desc": "Teachers with {CURRICULUM} expertise and classroom experience."
      },
      {
        "icon": "Users",
        "name": "Small group classes",
        "desc": "8-12 students per class. Your child gets seen, heard and responded to every lesson."
      },
      {
        "icon": "GraduationCap",
        "name": "{CURRICULUM} aligned",
        "desc": "Content mapped directly to {CURRICULUM} outcomes. Complements school, session by session."
      },
      {
        "icon": "MessageCircle",
        "name": "Feedback every lesson",
        "desc": "Structured written and verbal feedback on every student's work, every session."
      },
      {
        "icon": "FileText",
        "name": "End-of-term report",
        "desc": "A clear parent-facing progress summary at end of each term - before school reports arrive."
      },
      {
        "icon": "Target",
        "name": "Free assessment",
        "desc": "Level placement before the first session. No wasted lessons on work already mastered."
      },
      {
        "icon": "CheckCircle2",
        "name": "Year 5 NAPLAN ready",
        "desc": "Year 4 content sets the foundation for Year 5 NAPLAN - built into the term sequence."
      },
      {
        "icon": "Smartphone",
        "name": "Schoolola LMS",
        "desc": "Access theory lesson videos, online practice questions and track progress easily via the Schoolola LMS app.",
        "images": [
          "/lovable-uploads/lms-parent-dashboard.png",
          "/lovable-uploads/lms-learning-dashboard.png",
          "/lovable-uploads/lms-diagnostic-overview.png",
          "/lovable-uploads/lms-quiz-question-1.png",
          "/lovable-uploads/lms-quiz-question-2.png",
          "/lovable-uploads/lms-results-report.png"
        ]
      }
    ],
    "faq": [
      {
        "q": "Is {PRICE} the full price - are there any extras?",
        "a": "{PRICE} covers the session. Term enrolments include the end-of-term progress report at no extra cost. {BOOKS}"
      },
      {
        "q": "What study materials are provided?",
        "a": "Study materials are provided as bound books - no loose papers. {BOOKS}"
      },
      {
        "q": "How is this different from what happens at school?",
        "a": "Group sizes are smaller (8-12 students per class), lessons follow a consistent structured format, and every student receives written feedback on their own work each session. Content follows the same {CURRICULUM} outcomes - we're giving your child more practice and more attention within the same curriculum."
      },
      {
        "q": "What if my child misses a session?",
        "a": "We ask for consistent attendance as each session builds on the last. If a session is missed, your educator can note the key material to follow up at home - but there are no standard make-up classes. Contact your local centre."
      },
      {
        "q": "Can a confident Year 4 student still be challenged?",
        "a": "Yes. The assessment places students within ability groups, not just year groups. A Year 4 student working above level will be placed in a group that challenges them accordingly."
      }
    ],
    "siblingDesc": "Number, geometry and problem solving",
    "naplanRelated": false,
    "seoDescription": "Year 4 English tutoring in {STATE}. {CURRICULUM} aligned reading, writing, grammar with Year 5 NAPLAN readiness. Small groups of 8-12 students.",
    "schemaDescription": "Year 4 English tuition in {STATE}. Small groups of 8-12 students, aligned to the {CURRICULUM}. Reading, writing, grammar and Year 5 NAPLAN readiness.",
    "quickFacts": [
      {
        "icon": "Clock",
        "text": "60 min / session"
      },
      {
        "icon": "Users",
        "text": "8-12 students"
      },
      {
        "icon": "MapPin",
        "text": "In-centre + online"
      },
      {
        "icon": "DollarSign",
        "text": "{PRICE} / session"
      }
    ]
  },
  "4-maths": {
    "subtitle": "Extend number sense, problem-solving and reasoning skills in small groups - aligned to the {CURRICULUM} for Mathematics.",
    "tabs": [
      {
        "id": "term",
        "icon": "Calendar",
        "name": "Term course",
        "sub": "10 weekly sessions per term",
        "items": [
          {
            "label": "Overview",
            "name": "Course overview",
            "heading": "Term Course overview",
            "para": "The Year 4 Maths term course runs 10 sessions per term, {MINUTES} minutes each, in small groups of 8-12 students. Lessons are aligned to the {CURRICULUM} for Mathematics and consolidate the skills needed for Year 5 and beyond.",
            "bullets": [
              "Number and algebra, measurement and geometry, and statistics and probability covered across the term",
              "Content sequenced alongside the school year so tutoring and classroom work reinforce each other",
              "Strong preparation for Year 5 NAPLAN numeracy"
            ]
          },
          {
            "label": "Audience",
            "name": "Who it's for",
            "heading": "Who it's for",
            "para": "This course is ideal for students who…",
            "bullets": [
              "Want to get ahead of upcoming school maths content early",
              "Aim for stronger performance in class tasks and assessments",
              "Are building problem-solving confidence through regular guided practice",
              "(Parents) want progress to be visible and reported each term"
            ]
          },
          {
            "label": "Benefits",
            "name": "What you'll gain",
            "heading": "What you'll gain",
            "para": "By the end of the term your child will have made measurable progress in:",
            "bullets": [
              "Number sense - place value to tens of thousands, multi-digit addition, subtraction, multiplication and division",
              "Fractions and decimals - comparing, ordering and equivalent fractions",
              "Measurement and geometry - perimeter, area, angles, 2D and 3D shapes",
              "NAPLAN numeracy readiness - multi-step problem solving under timed conditions"
            ]
          },
          {
            "label": "Content",
            "name": "What's covered",
            "heading": "What's covered",
            "para": "Topics are sequenced across the four school terms:",
            "bullets": [
              "Term 1 - Number foundations: place value, addition, subtraction and mental strategies",
              "Term 2 - Multiplication, division and introduction to fractions and decimals",
              "Term 3 - Measurement, geometry and NAPLAN numeracy preparation",
              "Term 4 - Data, probability, time and consolidation of key strategies"
            ]
          },
          {
            "label": "Program",
            "name": "How it works",
            "heading": "How it works",
            "para": "Every session follows a consistent structure so students always know what to expect:",
            "bullets": [
              "Free level assessment before the first session - no wasted lessons on work already mastered",
              "Each {MINUTES}-min lesson: introduce the concept → worked examples → guided practice → independent problem solving",
              "Written and verbal feedback on every student's work each session",
              "End-of-term progress report so parents can see clearly what's improved"
            ]
          }
        ]
      },
      {
        "id": "holiday",
        "icon": "Sun",
        "name": "Holiday intensive",
        "sub": "4 daily lessons in the holidays",
        "items": [
          {
            "label": "Overview",
            "name": "Course overview",
            "heading": "Holiday intensive overview",
            "para": "The Year 4 Maths holiday intensive runs 4 daily sessions over the school holidays, {MINUTES} minutes each. The program focuses on one key strand intensively, allowing students to build skill and confidence quickly.",
            "bullets": [
              "4 sessions, daily, {MINUTES} minutes each",
              "One maths strand - deep focus rather than broad coverage",
              "Same small groups and qualified educators as the term course",
              "No prior Schoolola enrolment needed"
            ]
          },
          {
            "label": "Audience",
            "name": "Who it's for",
            "heading": "Who it's for",
            "para": "The holiday intensive suits students who…",
            "bullets": [
              "Are preparing for want targeted numeracy practice",
              "Want a focused short boost before the next school term begins",
              "Can't commit to a full term but want structured, quality tuition",
              "Need to build confidence in one specific maths topic quickly"
            ]
          },
          {
            "label": "Benefits",
            "name": "What you'll gain",
            "heading": "What you'll gain",
            "para": "After 4 sessions your child will have:",
            "bullets": [
              "A solid understanding of one strand - concepts, strategies, and worked examples",
              "Numeracy strategies for NAPLAN under timed conditions",
              "Increased confidence to tackle maths tasks at school",
              "Specific written feedback on their own practice problems"
            ]
          },
          {
            "label": "Content",
            "name": "What's covered",
            "heading": "What's covered",
            "para": "The four sessions follow a structured daily sequence:",
            "bullets": [
              "Day 1 - Strand overview: key concepts, worked examples, mental strategies",
              "Day 2 - Guided practice: scaffold problems, vocabulary and notation",
              "Day 3 - Problem solving: multi-step questions, reasoning and checking",
              "Day 4 - Practice task under assessment conditions with full written feedback"
            ]
          },
          {
            "label": "Program",
            "name": "How it works",
            "heading": "How it works",
            "para": "The intensive is designed for maximum impact across four consecutive sessions:",
            "bullets": [
              "Register your interest - sessions fill quickly in school holiday periods",
              "Each day builds on the previous - daily attendance is expected",
              "Individual written feedback on Day 3 and Day 4 tasks included",
              "Students leave with a completed problem-solving portfolio"
            ]
          }
        ]
      }
    ],
    "inclusions": [
      {
        "icon": "Award",
        "name": "Qualified educators",
        "desc": "Teachers with {CURRICULUM} expertise and classroom experience."
      },
      {
        "icon": "Users",
        "name": "Small group classes",
        "desc": "8-12 students per class. Your child gets seen, heard and responded to every lesson."
      },
      {
        "icon": "GraduationCap",
        "name": "{CURRICULUM} aligned",
        "desc": "Content mapped directly to {CURRICULUM} outcomes. Complements school, session by session."
      },
      {
        "icon": "MessageCircle",
        "name": "Feedback every lesson",
        "desc": "Structured written and verbal feedback on every student's work, every session."
      },
      {
        "icon": "FileText",
        "name": "End-of-term report",
        "desc": "A clear parent-facing progress summary at end of each term - before school reports arrive."
      },
      {
        "icon": "Target",
        "name": "Free assessment",
        "desc": "Level placement before the first session. No wasted lessons on work already mastered."
      },
      {
        "icon": "CheckCircle2",
        "name": "Year 5 NAPLAN ready",
        "desc": "Year 4 content sets the foundation for Year 5 NAPLAN numeracy - built into the term sequence."
      },
      {
        "icon": "Smartphone",
        "name": "Schoolola LMS",
        "desc": "Access theory lesson videos, online practice questions and track progress easily via the Schoolola LMS app.",
        "images": [
          "/lovable-uploads/lms-parent-dashboard.png",
          "/lovable-uploads/lms-learning-dashboard.png",
          "/lovable-uploads/lms-diagnostic-overview.png",
          "/lovable-uploads/lms-quiz-question-1.png",
          "/lovable-uploads/lms-quiz-question-2.png",
          "/lovable-uploads/lms-results-report.png"
        ]
      }
    ],
    "faq": [
      {
        "q": "Is {PRICE} the full price - are there any extras?",
        "a": "{PRICE} covers the session. Term enrolments include the end-of-term progress report at no extra cost. {BOOKS}"
      },
      {
        "q": "What study materials are provided?",
        "a": "Study materials are provided as bound books - no loose papers. {BOOKS}"
      },
      {
        "q": "How is this different from what happens at school?",
        "a": "Group sizes are smaller (8-12 students per class), lessons follow a consistent structured format, and every student receives written feedback on their own work each session. Content follows the same {CURRICULUM} outcomes - we're giving your child more practice and more attention within the same curriculum."
      },
      {
        "q": "What if my child misses a session?",
        "a": "We ask for consistent attendance as each session builds on the last. If a session is missed, your educator can note the key material to follow up at home - but there are no standard make-up classes. Contact your local centre."
      },
      {
        "q": "Can a confident Year 4 student still be challenged?",
        "a": "Yes. The assessment places students within ability groups, not just year groups. A Year 4 student working above level will be placed in a group that challenges them accordingly."
      }
    ],
    "siblingDesc": "Reading, writing and grammar foundations",
    "naplanRelated": false,
    "seoDescription": "Year 4 Maths tutoring in {STATE}. {CURRICULUM} aligned number, geometry, problem solving with Year 5 NAPLAN numeracy readiness. Small groups of 8-12 students.",
    "schemaDescription": "Year 4 Maths tuition in {STATE}. Small groups of 8-12 students, aligned to the {CURRICULUM}. Number, geometry, problem solving and Year 5 NAPLAN numeracy readiness.",
    "quickFacts": [
      {
        "icon": "Clock",
        "text": "60 min / session"
      },
      {
        "icon": "Users",
        "text": "8-12 students"
      },
      {
        "icon": "MapPin",
        "text": "In-centre + online"
      },
      {
        "icon": "DollarSign",
        "text": "{PRICE} / session"
      }
    ]
  },
  "5-english": {
    "subtitle": "Sharpen reading comprehension, writing and grammar in small groups - aligned to the {CURRICULUM} for English, with Year 5 NAPLAN preparation built in.",
    "tabs": [
      {
        "id": "term",
        "icon": "Calendar",
        "name": "Term course",
        "sub": "10 weekly sessions per term",
        "items": [
          {
            "label": "Overview",
            "name": "Course overview",
            "heading": "Term Course overview",
            "para": "The Year 5 English term course runs 10 sessions per term, {MINUTES} minutes each, in small groups of 8-12 students. Lessons are aligned to the {CURRICULUM} for English and lay the foundation for Year 6.",
            "bullets": [
              "Reading comprehension, writing and grammar covered across the term",
              "Content sequenced alongside the school year so tutoring and classroom work reinforce each other",
              "Strong preparation for Year 5 NAPLAN"
            ]
          },
          {
            "label": "Audience",
            "name": "Who it's for",
            "heading": "Who it's for",
            "para": "This course is ideal for students who…",
            "bullets": [
              "Are sitting Year 5 NAPLAN and want structured, targeted practice",
              "Aim for stronger performance in class tasks and assessments",
              "Are building writing confidence through regular guided practice",
              "(Parents) want progress to be visible and reported each term"
            ]
          },
          {
            "label": "Benefits",
            "name": "What you'll gain",
            "heading": "What you'll gain",
            "para": "By the end of the term your child will have made measurable progress in:",
            "bullets": [
              "Reading comprehension - inferential reasoning, text analysis, advanced vocabulary",
              "Writing - narrative, informative and persuasive texts with structured feedback on each",
              "Grammar and punctuation - complex sentences, paragraphing, editing skills",
              "NAPLAN readiness - reading and writing under timed conditions"
            ]
          },
          {
            "label": "Content",
            "name": "What's covered",
            "heading": "What's covered",
            "para": "Topics are sequenced across the four school terms:",
            "bullets": [
              "Term 1 - Reading: inferential comprehension, vocabulary, text analysis",
              "Term 2 - Writing: narrative craft, character development, descriptive language",
              "Term 3 - Persuasive writing and Year 5 NAPLAN preparation",
              "Term 4 - Language conventions"
            ]
          },
          {
            "label": "Program",
            "name": "How it works",
            "heading": "How it works",
            "para": "Every session follows a consistent structure so students always know what to expect:",
            "bullets": [
              "Free level assessment before the first session - no wasted lessons on work already mastered",
              "Each {MINUTES}-min lesson: introduce the concept → model examples → guided practice → independent application",
              "Written and verbal feedback on every student's work each session",
              "End-of-term progress report so parents can see clearly what's improved"
            ]
          }
        ]
      },
      {
        "id": "holiday",
        "icon": "Sun",
        "name": "Holiday intensive",
        "sub": "4 daily lessons in the holidays",
        "items": [
          {
            "label": "Overview",
            "name": "Course overview",
            "heading": "Holiday intensive overview",
            "para": "The Year 5 English holiday intensive runs 4 daily sessions over the school holidays, {MINUTES} minutes each. The program focuses on one writing genre intensively, allowing students to build skill and confidence quickly.",
            "bullets": [
              "4 sessions, daily, {MINUTES} minutes each",
              "One writing genre - deep focus rather than broad coverage",
              "Same small groups and qualified educators as the term course",
              "No prior Schoolola enrolment needed"
            ]
          },
          {
            "label": "Audience",
            "name": "Who it's for",
            "heading": "Who it's for",
            "para": "The holiday intensive suits students who…",
            "bullets": [
              "Are preparing for NAPLAN and want targeted writing practice",
              "Want a focused short boost before the next school term begins",
              "Can't commit to a full term but want structured, quality tuition",
              "Need to build confidence in one specific writing type quickly"
            ]
          },
          {
            "label": "Benefits",
            "name": "What you'll gain",
            "heading": "What you'll gain",
            "para": "After 4 sessions your child will have:",
            "bullets": [
              "A solid understanding of one writing genre - structure, language features, planning approach",
              "Writing strategies for NAPLAN under timed conditions",
              "Increased confidence to tackle writing tasks at school",
              "Specific written feedback on their own practice writing"
            ]
          },
          {
            "label": "Content",
            "name": "What's covered",
            "heading": "What's covered",
            "para": "The four sessions follow a structured daily sequence:",
            "bullets": [
              "Day 1 - Genre overview: model texts, key features, planning framework",
              "Day 2 - Planning and drafting: scaffold practice, vocabulary development",
              "Day 3 - Revision and editing: sentence variety, language choices, checking against criteria",
              "Day 4 - Practice task under assessment conditions with full written feedback"
            ]
          },
          {
            "label": "Program",
            "name": "How it works",
            "heading": "How it works",
            "para": "The intensive is designed for maximum impact across four consecutive sessions:",
            "bullets": [
              "Register your interest - sessions fill quickly in school holiday periods",
              "Each day builds on the previous - daily attendance is expected",
              "Individual written feedback on Day 3 and Day 4 tasks included",
              "Students leave with a completed writing portfolio piece"
            ]
          }
        ]
      }
    ],
    "inclusions": [
      {
        "icon": "Award",
        "name": "Qualified educators",
        "desc": "Teachers with {CURRICULUM} expertise and classroom experience."
      },
      {
        "icon": "Users",
        "name": "Small group classes",
        "desc": "8-12 students per class. Your child gets seen, heard and responded to every lesson."
      },
      {
        "icon": "GraduationCap",
        "name": "{CURRICULUM} aligned",
        "desc": "Content mapped directly to {CURRICULUM} outcomes. Complements school, session by session."
      },
      {
        "icon": "MessageCircle",
        "name": "Feedback every lesson",
        "desc": "Structured written and verbal feedback on every student's work, every session."
      },
      {
        "icon": "FileText",
        "name": "End-of-term report",
        "desc": "A clear parent-facing progress summary at end of each term - before school reports arrive."
      },
      {
        "icon": "Target",
        "name": "Free assessment",
        "desc": "Level placement before the first session. No wasted lessons on work already mastered."
      },
      {
        "icon": "CheckCircle2",
        "name": "NAPLAN ready",
        "desc": "Year 5 content directly supports Year 5 NAPLAN - built into the term sequence."
      },
      {
        "icon": "Smartphone",
        "name": "Schoolola LMS",
        "desc": "Access theory lesson videos, online practice questions and track progress easily via the Schoolola LMS app.",
        "images": [
          "/lovable-uploads/lms-parent-dashboard.png",
          "/lovable-uploads/lms-learning-dashboard.png",
          "/lovable-uploads/lms-diagnostic-overview.png",
          "/lovable-uploads/lms-quiz-question-1.png",
          "/lovable-uploads/lms-quiz-question-2.png",
          "/lovable-uploads/lms-results-report.png"
        ]
      }
    ],
    "faq": [
      {
        "q": "Is {PRICE} the full price - are there any extras?",
        "a": "{PRICE} covers the session. Term enrolments include the end-of-term progress report at no extra cost. {BOOKS}"
      },
      {
        "q": "What study materials are provided?",
        "a": "Study materials are provided as bound books - no loose papers. {BOOKS}"
      },
      {
        "q": "How is this different from what happens at school?",
        "a": "Group sizes are smaller (8-12 students per class), lessons follow a consistent structured format, and every student receives written feedback on their own work each session. Content follows the same {CURRICULUM} outcomes - we're giving your child more practice and more attention within the same curriculum."
      },
      {
        "q": "What if my child misses a session?",
        "a": "We ask for consistent attendance as each session builds on the last. If a session is missed, your educator can note the key material to follow up at home - but there are no standard make-up classes. Contact your local centre."
      },
      {
        "q": "Can a confident Year 5 student still be challenged?",
        "a": "Yes. The assessment places students within ability groups, not just year groups. A Year 5 student working above level will be placed in a group that challenges them accordingly."
      }
    ],
    "siblingDesc": "Fractions, decimals and problem solving",
    "naplanRelated": false,
    "seoDescription": "Year 5 English tutoring in {STATE}. {CURRICULUM} aligned reading, writing and grammar with Year 5 NAPLAN readiness. Small groups of 8-12 students.",
    "schemaDescription": "Year 5 English tuition in {STATE}. Small groups of 8-12 students, aligned to the {CURRICULUM}. Reading, writing, grammar and NAPLAN readiness.",
    "quickFacts": [
      {
        "icon": "Clock",
        "text": "60 min / session"
      },
      {
        "icon": "Users",
        "text": "8-12 students"
      },
      {
        "icon": "MapPin",
        "text": "In-centre + online"
      },
      {
        "icon": "DollarSign",
        "text": "{PRICE} / session"
      }
    ]
  },
  "5-maths": {
    "subtitle": "Extend fractions, decimals, geometry and problem-solving in small groups - aligned to the {CURRICULUM} for Mathematics, with Year 5 NAPLAN numeracy readiness.",
    "tabs": [
      {
        "id": "term",
        "icon": "Calendar",
        "name": "Term course",
        "sub": "10 weekly sessions per term",
        "items": [
          {
            "label": "Overview",
            "name": "Course overview",
            "heading": "Term Course overview",
            "para": "The Year 5 Maths term course runs 10 sessions per term, {MINUTES} minutes each, in small groups of 8-12 students. Lessons are aligned to the {CURRICULUM} for Mathematics and prepare students for Year 5 NAPLAN.",
            "bullets": [
              "Number and algebra, measurement and geometry, and statistics and probability covered across the term",
              "Content sequenced alongside the school year so tutoring and classroom work reinforce each other",
              "Strong preparation for Year 5 NAPLAN numeracy"
            ]
          },
          {
            "label": "Audience",
            "name": "Who it's for",
            "heading": "Who it's for",
            "para": "This course is ideal for students who…",
            "bullets": [
              "Are sitting Year 5 NAPLAN and want structured numeracy practice",
              "Aim for stronger performance in class tasks and assessments",
              "Are building problem-solving confidence through regular guided practice",
              "(Parents) want progress to be visible and reported each term"
            ]
          },
          {
            "label": "Benefits",
            "name": "What you'll gain",
            "heading": "What you'll gain",
            "para": "By the end of the term your child will have made measurable progress in:",
            "bullets": [
              "Number sense - large numbers, multi-digit multiplication and division, order of operations",
              "Fractions, decimals and percentages - equivalence, operations and real-world application",
              "Measurement and geometry - area, volume, angles, 2D and 3D shapes",
              "NAPLAN readiness - multi-step problem solving under timed conditions"
            ]
          },
          {
            "label": "Content",
            "name": "What's covered",
            "heading": "What's covered",
            "para": "Topics are sequenced across the four school terms:",
            "bullets": [
              "Term 1 - Number foundations: place value, four operations, mental strategies",
              "Term 2 - Fractions, decimals and percentages",
              "Term 3 - Measurement, geometry and Year 5 NAPLAN numeracy preparation",
              "Term 4 - Data and probability"
            ]
          },
          {
            "label": "Program",
            "name": "How it works",
            "heading": "How it works",
            "para": "Every session follows a consistent structure so students always know what to expect:",
            "bullets": [
              "Free level assessment before the first session - no wasted lessons on work already mastered",
              "Each {MINUTES}-min lesson: introduce the concept → worked examples → guided practice → independent problem solving",
              "Written and verbal feedback on every student's work each session",
              "End-of-term progress report so parents can see clearly what's improved"
            ]
          }
        ]
      },
      {
        "id": "holiday",
        "icon": "Sun",
        "name": "Holiday intensive",
        "sub": "4 daily lessons in the holidays",
        "items": [
          {
            "label": "Overview",
            "name": "Course overview",
            "heading": "Holiday intensive overview",
            "para": "The Year 5 Maths holiday intensive runs 4 daily sessions over the school holidays, {MINUTES} minutes each. The program focuses on one key strand intensively, allowing students to build skill and confidence quickly.",
            "bullets": [
              "4 sessions, daily, {MINUTES} minutes each",
              "One maths strand - deep focus rather than broad coverage",
              "Same small groups and qualified educators as the term course",
              "No prior Schoolola enrolment needed"
            ]
          },
          {
            "label": "Audience",
            "name": "Who it's for",
            "heading": "Who it's for",
            "para": "The holiday intensive suits students who…",
            "bullets": [
              "Are preparing for NAPLAN and want targeted numeracy practice",
              "Want a focused short boost before the next school term begins",
              "Can't commit to a full term but want structured, quality tuition",
              "Need to build confidence in one specific maths topic quickly"
            ]
          },
          {
            "label": "Benefits",
            "name": "What you'll gain",
            "heading": "What you'll gain",
            "para": "After 4 sessions your child will have:",
            "bullets": [
              "A solid understanding of one strand - concepts, strategies and worked examples",
              "Numeracy strategies for NAPLAN under timed conditions",
              "Increased confidence to tackle maths tasks at school",
              "Specific written feedback on their own practice problems"
            ]
          },
          {
            "label": "Content",
            "name": "What's covered",
            "heading": "What's covered",
            "para": "The four sessions follow a structured daily sequence:",
            "bullets": [
              "Day 1 - Strand overview: key concepts, worked examples, mental strategies",
              "Day 2 - Guided practice: scaffold problems, vocabulary and notation",
              "Day 3 - Problem solving: multi-step questions, reasoning and checking",
              "Day 4 - Practice task under assessment conditions with full written feedback"
            ]
          },
          {
            "label": "Program",
            "name": "How it works",
            "heading": "How it works",
            "para": "The intensive is designed for maximum impact across four consecutive sessions:",
            "bullets": [
              "Register your interest - sessions fill quickly in school holiday periods",
              "Each day builds on the previous - daily attendance is expected",
              "Individual written feedback on Day 3 and Day 4 tasks included",
              "Students leave with a completed problem-solving portfolio"
            ]
          }
        ]
      }
    ],
    "inclusions": [
      {
        "icon": "Award",
        "name": "Qualified educators",
        "desc": "Teachers with {CURRICULUM} expertise and classroom experience."
      },
      {
        "icon": "Users",
        "name": "Small group classes",
        "desc": "8-12 students per class. Your child gets seen, heard and responded to every lesson."
      },
      {
        "icon": "GraduationCap",
        "name": "{CURRICULUM} aligned",
        "desc": "Content mapped directly to {CURRICULUM} outcomes. Complements school, session by session."
      },
      {
        "icon": "MessageCircle",
        "name": "Feedback every lesson",
        "desc": "Structured written and verbal feedback on every student's work, every session."
      },
      {
        "icon": "FileText",
        "name": "End-of-term report",
        "desc": "A clear parent-facing progress summary at end of each term - before school reports arrive."
      },
      {
        "icon": "Target",
        "name": "Free assessment",
        "desc": "Level placement before the first session. No wasted lessons on work already mastered."
      },
      {
        "icon": "CheckCircle2",
        "name": "NAPLAN ready",
        "desc": "Year 5 content directly supports Year 5 NAPLAN numeracy - built into the term sequence."
      },
      {
        "icon": "Smartphone",
        "name": "Schoolola LMS",
        "desc": "Access theory lesson videos, online practice questions and track progress easily via the Schoolola LMS app.",
        "images": [
          "/lovable-uploads/lms-parent-dashboard.png",
          "/lovable-uploads/lms-learning-dashboard.png",
          "/lovable-uploads/lms-diagnostic-overview.png",
          "/lovable-uploads/lms-quiz-question-1.png",
          "/lovable-uploads/lms-quiz-question-2.png",
          "/lovable-uploads/lms-results-report.png"
        ]
      }
    ],
    "faq": [
      {
        "q": "Is {PRICE} the full price - are there any extras?",
        "a": "{PRICE} covers the session. Term enrolments include the end-of-term progress report at no extra cost. {BOOKS}"
      },
      {
        "q": "What study materials are provided?",
        "a": "Study materials are provided as bound books - no loose papers. {BOOKS}"
      },
      {
        "q": "How is this different from what happens at school?",
        "a": "Group sizes are smaller (8-12 students per class), lessons follow a consistent structured format, and every student receives written feedback on their own work each session. Content follows the same {CURRICULUM} outcomes - we're giving your child more practice and more attention within the same curriculum."
      },
      {
        "q": "What if my child misses a session?",
        "a": "We ask for consistent attendance as each session builds on the last. If a session is missed, your educator can note the key material to follow up at home - but there are no standard make-up classes. Contact your local centre."
      },
      {
        "q": "Can a confident Year 5 student still be challenged?",
        "a": "Yes. The assessment places students within ability groups, not just year groups. A Year 5 student working above level will be placed in a group that challenges them accordingly."
      }
    ],
    "siblingDesc": "Reading, writing and grammar foundations",
    "naplanRelated": false,
    "seoDescription": "Year 5 Maths tutoring in {STATE}. {CURRICULUM} aligned number, fractions, geometry and problem solving with Year 5 NAPLAN readiness. Small groups of 8-12.",
    "schemaDescription": "Year 5 Maths tuition in {STATE}. Small groups of 8-12 students, aligned to the {CURRICULUM}. Number, fractions, geometry, problem solving and NAPLAN readiness.",
    "quickFacts": [
      {
        "icon": "Clock",
        "text": "60 min / session"
      },
      {
        "icon": "Users",
        "text": "8-12 students"
      },
      {
        "icon": "MapPin",
        "text": "In-centre + online"
      },
      {
        "icon": "DollarSign",
        "text": "{PRICE} / session"
      }
    ]
  },
  "6-english": {
    "subtitle": "Consolidate reading, writing and grammar in small groups - aligned to the {CURRICULUM} for English and a strong transition into high school English.",
    "tabs": [
      {
        "id": "term",
        "icon": "Calendar",
        "name": "Term course",
        "sub": "10 weekly sessions per term",
        "items": [
          {
            "label": "Overview",
            "name": "Course overview",
            "heading": "Term Course overview",
            "para": "The Year 6 English term course runs 10 sessions per term, {MINUTES} minutes each, in small groups of 8-12 students. Lessons consolidate {CURRICULUM} outcomes and prepare students for the transition into Year 7 English.",
            "bullets": [
              "Reading comprehension, writing and grammar covered across the term",
              "Content sequenced alongside the school year so tutoring and classroom work reinforce each other",
              "Strong preparation for the move into Year 7"
            ]
          },
          {
            "label": "Audience",
            "name": "Who it's for",
            "heading": "Who it's for",
            "para": "This course is ideal for students who…",
            "bullets": [
              "Aim for stronger performance in class tasks and assessments",
              "Want a smooth transition into Year 7 English",
              "Are building writing confidence through regular guided practice",
              "(Parents) want progress to be visible and reported each term"
            ]
          },
          {
            "label": "Benefits",
            "name": "What you'll gain",
            "heading": "What you'll gain",
            "para": "By the end of the term your child will have made measurable progress in:",
            "bullets": [
              "Reading comprehension - inferential reasoning, text analysis, advanced vocabulary",
              "Writing - narrative, informative and persuasive texts with structured feedback on each",
              "Grammar and punctuation - complex sentences, paragraphing, editing skills",
              "High school readiness - extended writing under timed conditions"
            ]
          },
          {
            "label": "Content",
            "name": "What's covered",
            "heading": "What's covered",
            "para": "Topics are sequenced across the four school terms:",
            "bullets": [
              "Term 1 - Reading: inferential comprehension, vocabulary, text analysis",
              "Term 2 - Writing: narrative craft, character development, descriptive language",
              "Term 3 - Persuasive and analytical writing",
              "Term 4 - Year 7 transition: extended responses, grammar and editing for high school"
            ]
          },
          {
            "label": "Program",
            "name": "How it works",
            "heading": "How it works",
            "para": "Every session follows a consistent structure so students always know what to expect:",
            "bullets": [
              "Free level assessment before the first session - no wasted lessons on work already mastered",
              "Each {MINUTES}-min lesson: introduce the concept → model examples → guided practice → independent application",
              "Written and verbal feedback on every student's work each session",
              "End-of-term progress report so parents can see clearly what's improved"
            ]
          }
        ]
      },
      {
        "id": "holiday",
        "icon": "Sun",
        "name": "Holiday intensive",
        "sub": "4 daily lessons in the holidays",
        "items": [
          {
            "label": "Overview",
            "name": "Course overview",
            "heading": "Holiday intensive overview",
            "para": "The Year 6 English holiday intensive runs 4 daily sessions over the school holidays, {MINUTES} minutes each. The program focuses on one writing genre intensively, allowing students to build skill and confidence quickly.",
            "bullets": [
              "4 sessions, daily, {MINUTES} minutes each",
              "One writing genre - deep focus rather than broad coverage",
              "Same small groups and qualified educators as the term course",
              "No prior Schoolola enrolment needed"
            ]
          },
          {
            "label": "Audience",
            "name": "Who it's for",
            "heading": "Who it's for",
            "para": "The holiday intensive suits students who…",
            "bullets": [
              "Are preparing for upcoming assessments and want targeted writing practice",
              "Want a focused short boost before the next school term begins",
              "Can't commit to a full term but want structured, quality tuition",
              "Need to build confidence in one specific writing type quickly"
            ]
          },
          {
            "label": "Benefits",
            "name": "What you'll gain",
            "heading": "What you'll gain",
            "para": "After 4 sessions your child will have:",
            "bullets": [
              "A solid understanding of one writing genre - structure, language features, planning approach",
              "Writing strategies for high school under timed conditions",
              "Increased confidence to tackle writing tasks at school",
              "Specific written feedback on their own practice writing"
            ]
          },
          {
            "label": "Content",
            "name": "What's covered",
            "heading": "What's covered",
            "para": "The four sessions follow a structured daily sequence:",
            "bullets": [
              "Day 1 - Genre overview: model texts, key features, planning framework",
              "Day 2 - Planning and drafting: scaffold practice, vocabulary development",
              "Day 3 - Revision and editing: sentence variety, language choices, checking against criteria",
              "Day 4 - Practice task under assessment conditions with full written feedback"
            ]
          },
          {
            "label": "Program",
            "name": "How it works",
            "heading": "How it works",
            "para": "The intensive is designed for maximum impact across four consecutive sessions:",
            "bullets": [
              "Register your interest - sessions fill quickly in school holiday periods",
              "Each day builds on the previous - daily attendance is expected",
              "Individual written feedback on Day 3 and Day 4 tasks included",
              "Students leave with a completed writing portfolio piece"
            ]
          }
        ]
      }
    ],
    "inclusions": [
      {
        "icon": "Award",
        "name": "Qualified educators",
        "desc": "Teachers with {CURRICULUM} expertise and classroom experience."
      },
      {
        "icon": "Users",
        "name": "Small group classes",
        "desc": "8-12 students per class. Your child gets seen, heard and responded to every lesson."
      },
      {
        "icon": "GraduationCap",
        "name": "{CURRICULUM} aligned",
        "desc": "Content mapped directly to {CURRICULUM} outcomes. Complements school, session by session."
      },
      {
        "icon": "MessageCircle",
        "name": "Feedback every lesson",
        "desc": "Structured written and verbal feedback on every student's work, every session."
      },
      {
        "icon": "FileText",
        "name": "End-of-term report",
        "desc": "A clear parent-facing progress summary at end of each term - before school reports arrive."
      },
      {
        "icon": "Target",
        "name": "Free assessment",
        "desc": "Level placement before the first session. No wasted lessons on work already mastered."
      },
      {
        "icon": "CheckCircle2",
        "name": "High school ready",
        "desc": "Year 6 content sets a strong foundation for Year 7 English."
      },
      {
        "icon": "Smartphone",
        "name": "Schoolola LMS",
        "desc": "Access theory lesson videos, online practice questions and track progress easily via the Schoolola LMS app.",
        "images": [
          "/lovable-uploads/lms-parent-dashboard.png",
          "/lovable-uploads/lms-learning-dashboard.png",
          "/lovable-uploads/lms-diagnostic-overview.png",
          "/lovable-uploads/lms-quiz-question-1.png",
          "/lovable-uploads/lms-quiz-question-2.png",
          "/lovable-uploads/lms-results-report.png"
        ]
      }
    ],
    "faq": [
      {
        "q": "Is {PRICE} the full price - are there any extras?",
        "a": "{PRICE} covers the session. Term enrolments include the end-of-term progress report at no extra cost. {BOOKS}"
      },
      {
        "q": "What study materials are provided?",
        "a": "Study materials are provided as bound books - no loose papers. {BOOKS}"
      },
      {
        "q": "How is this different from what happens at school?",
        "a": "Group sizes are smaller (8-12 students per class), lessons follow a consistent structured format, and every student receives written feedback on their own work each session. Content follows the same {CURRICULUM} outcomes - we're giving your child more practice and more attention within the same curriculum."
      },
      {
        "q": "What if my child misses a session?",
        "a": "We ask for consistent attendance as each session builds on the last. If a session is missed, your educator can note the key material to follow up at home - but there are no standard make-up classes. Contact your local centre."
      },
      {
        "q": "Can a confident Year 6 student still be challenged?",
        "a": "Yes. The assessment places students within ability groups, not just year groups. A Year 6 student working above level will be placed in a group that challenges them accordingly."
      }
    ],
    "siblingDesc": "Fractions, algebra readiness and problem solving",
    "naplanRelated": false,
    "seoDescription": "Year 6 English tutoring in {STATE}. {CURRICULUM} aligned reading, writing and grammar with high school readiness. Small groups of 8-12 students.",
    "schemaDescription": "Year 6 English tuition in {STATE}. Small groups of 8-12 students, aligned to the {CURRICULUM}. Reading, writing, grammar and Year 7 readiness.",
    "quickFacts": [
      {
        "icon": "Clock",
        "text": "60 min / session"
      },
      {
        "icon": "Users",
        "text": "8-12 students"
      },
      {
        "icon": "MapPin",
        "text": "In-centre + online"
      },
      {
        "icon": "DollarSign",
        "text": "{PRICE} / session"
      }
    ]
  },
  "6-maths": {
    "subtitle": "Consolidate number, fractions, geometry and problem-solving in small groups - aligned to the {CURRICULUM} for Mathematics, with a strong bridge to Year 7 algebra.",
    "tabs": [
      {
        "id": "term",
        "icon": "Calendar",
        "name": "Term course",
        "sub": "10 weekly sessions per term",
        "items": [
          {
            "label": "Overview",
            "name": "Course overview",
            "heading": "Term Course overview",
            "para": "The Year 6 Maths term course runs 10 sessions per term, {MINUTES} minutes each, in small groups of 8-12 students. Lessons consolidate {CURRICULUM} outcomes and bridge students into Year 7 algebra and high school maths.",
            "bullets": [
              "Number and algebra, measurement and geometry, and statistics and probability covered across the term",
              "Content sequenced alongside the school year so tutoring and classroom work reinforce each other",
              "Strong preparation for the move into Year 7"
            ]
          },
          {
            "label": "Audience",
            "name": "Who it's for",
            "heading": "Who it's for",
            "para": "This course is ideal for students who…",
            "bullets": [
              "Aim for stronger performance in class tasks and assessments",
              "Want a smooth transition into Year 7 maths",
              "Are building problem-solving confidence through regular guided practice",
              "(Parents) want progress to be visible and reported each term"
            ]
          },
          {
            "label": "Benefits",
            "name": "What you'll gain",
            "heading": "What you'll gain",
            "para": "By the end of the term your child will have made measurable progress in:",
            "bullets": [
              "Number sense - operations with large numbers, integers, order of operations",
              "Fractions, decimals, percentages and ratio - equivalence and real-world application",
              "Measurement and geometry - area, volume, angles, transformations",
              "Pre-algebra and reasoning - patterns, expressions and multi-step problem solving"
            ]
          },
          {
            "label": "Content",
            "name": "What's covered",
            "heading": "What's covered",
            "para": "Topics are sequenced across the four school terms:",
            "bullets": [
              "Term 1 - Number foundations: four operations, integers, mental strategies",
              "Term 2 - Fractions, decimals, percentages and ratio",
              "Term 3 - Measurement, geometry and mathematical reasoning",
              "Term 4 - Pre-algebra, data and probability - bridging to Year 7"
            ]
          },
          {
            "label": "Program",
            "name": "How it works",
            "heading": "How it works",
            "para": "Every session follows a consistent structure so students always know what to expect:",
            "bullets": [
              "Free level assessment before the first session - no wasted lessons on work already mastered",
              "Each {MINUTES}-min lesson: introduce the concept → worked examples → guided practice → independent problem solving",
              "Written and verbal feedback on every student's work each session",
              "End-of-term progress report so parents can see clearly what's improved"
            ]
          }
        ]
      },
      {
        "id": "holiday",
        "icon": "Sun",
        "name": "Holiday intensive",
        "sub": "4 daily lessons in the holidays",
        "items": [
          {
            "label": "Overview",
            "name": "Course overview",
            "heading": "Holiday intensive overview",
            "para": "The Year 6 Maths holiday intensive runs 4 daily sessions over the school holidays, {MINUTES} minutes each. The program focuses on one key strand intensively, allowing students to build skill and confidence quickly.",
            "bullets": [
              "4 sessions, daily, {MINUTES} minutes each",
              "One maths strand - deep focus rather than broad coverage",
              "Same small groups and qualified educators as the term course",
              "No prior Schoolola enrolment needed"
            ]
          },
          {
            "label": "Audience",
            "name": "Who it's for",
            "heading": "Who it's for",
            "para": "The holiday intensive suits students who…",
            "bullets": [
              "Are preparing for upcoming assessments and want targeted numeracy practice",
              "Want a focused short boost before the next school term begins",
              "Can't commit to a full term but want structured, quality tuition",
              "Need to build confidence in one specific maths topic quickly"
            ]
          },
          {
            "label": "Benefits",
            "name": "What you'll gain",
            "heading": "What you'll gain",
            "para": "After 4 sessions your child will have:",
            "bullets": [
              "A solid understanding of one strand - concepts, strategies and worked examples",
              "Numeracy strategies for high school under timed conditions",
              "Increased confidence to tackle maths tasks at school",
              "Specific written feedback on their own practice problems"
            ]
          },
          {
            "label": "Content",
            "name": "What's covered",
            "heading": "What's covered",
            "para": "The four sessions follow a structured daily sequence:",
            "bullets": [
              "Day 1 - Strand overview: key concepts, worked examples, mental strategies",
              "Day 2 - Guided practice: scaffold problems, vocabulary and notation",
              "Day 3 - Problem solving: multi-step questions, reasoning and checking",
              "Day 4 - Practice task under assessment conditions with full written feedback"
            ]
          },
          {
            "label": "Program",
            "name": "How it works",
            "heading": "How it works",
            "para": "The intensive is designed for maximum impact across four consecutive sessions:",
            "bullets": [
              "Register your interest - sessions fill quickly in school holiday periods",
              "Each day builds on the previous - daily attendance is expected",
              "Individual written feedback on Day 3 and Day 4 tasks included",
              "Students leave with a completed problem-solving portfolio"
            ]
          }
        ]
      }
    ],
    "inclusions": [
      {
        "icon": "Award",
        "name": "Qualified educators",
        "desc": "Teachers with {CURRICULUM} expertise and classroom experience."
      },
      {
        "icon": "Users",
        "name": "Small group classes",
        "desc": "8-12 students per class. Your child gets seen, heard and responded to every lesson."
      },
      {
        "icon": "GraduationCap",
        "name": "{CURRICULUM} aligned",
        "desc": "Content mapped directly to {CURRICULUM} outcomes. Complements school, session by session."
      },
      {
        "icon": "MessageCircle",
        "name": "Feedback every lesson",
        "desc": "Structured written and verbal feedback on every student's work, every session."
      },
      {
        "icon": "FileText",
        "name": "End-of-term report",
        "desc": "A clear parent-facing progress summary at end of each term - before school reports arrive."
      },
      {
        "icon": "Target",
        "name": "Free assessment",
        "desc": "Level placement before the first session. No wasted lessons on work already mastered."
      },
      {
        "icon": "CheckCircle2",
        "name": "Year 7 ready",
        "desc": "Year 6 content bridges students into Year 7 algebra."
      },
      {
        "icon": "Smartphone",
        "name": "Schoolola LMS",
        "desc": "Access theory lesson videos, online practice questions and track progress easily via the Schoolola LMS app.",
        "images": [
          "/lovable-uploads/lms-parent-dashboard.png",
          "/lovable-uploads/lms-learning-dashboard.png",
          "/lovable-uploads/lms-diagnostic-overview.png",
          "/lovable-uploads/lms-quiz-question-1.png",
          "/lovable-uploads/lms-quiz-question-2.png",
          "/lovable-uploads/lms-results-report.png"
        ]
      }
    ],
    "faq": [
      {
        "q": "Is {PRICE} the full price - are there any extras?",
        "a": "{PRICE} covers the session. Term enrolments include the end-of-term progress report at no extra cost. {BOOKS}"
      },
      {
        "q": "What study materials are provided?",
        "a": "Study materials are provided as bound books - no loose papers. {BOOKS}"
      },
      {
        "q": "How is this different from what happens at school?",
        "a": "Group sizes are smaller (8-12 students per class), lessons follow a consistent structured format, and every student receives written feedback on their own work each session. Content follows the same {CURRICULUM} outcomes - we're giving your child more practice and more attention within the same curriculum."
      },
      {
        "q": "What if my child misses a session?",
        "a": "We ask for consistent attendance as each session builds on the last. If a session is missed, your educator can note the key material to follow up at home - but there are no standard make-up classes. Contact your local centre."
      },
      {
        "q": "Can a confident Year 6 student still be challenged?",
        "a": "Yes. The assessment places students within ability groups, not just year groups. A Year 6 student working above level will be placed in a group that challenges them accordingly."
      }
    ],
    "siblingDesc": "Reading, writing and grammar foundations",
    "naplanRelated": false,
    "seoDescription": "Year 6 Maths tutoring in {STATE}. {CURRICULUM} aligned number, fractions, geometry and pre-algebra with Year 7 readiness. Small groups of 8-12 students.",
    "schemaDescription": "Year 6 Maths tuition in {STATE}. Small groups of 8-12 students, aligned to the {CURRICULUM}. Number, fractions, geometry, pre-algebra and Year 7 readiness.",
    "quickFacts": [
      {
        "icon": "Clock",
        "text": "60 min / session"
      },
      {
        "icon": "Users",
        "text": "8-12 students"
      },
      {
        "icon": "MapPin",
        "text": "In-centre + online"
      },
      {
        "icon": "DollarSign",
        "text": "{PRICE} / session"
      }
    ]
  },
  "7-english": {
    "subtitle": "Build high school English habits - analytical reading, extended writing and language conventions - in small groups, aligned to the {CURRICULUM} for English.",
    "tabs": [
      {
        "id": "term",
        "icon": "Calendar",
        "name": "Term course",
        "sub": "10 weekly sessions per term",
        "items": [
          {
            "label": "Overview",
            "name": "Course overview",
            "heading": "Term Course overview",
            "para": "The Year 7 English term course runs 10 sessions per term, {MINUTES} minutes each, in small groups of 8-12 students. Lessons are aligned to the {CURRICULUM} for English and build the analytical and writing habits expected in high school English.",
            "bullets": [
              "Analytical reading, extended writing and grammar covered across the term",
              "Content sequenced alongside the school year so tutoring and classroom work reinforce each other",
              "Strong preparation for Year 7 NAPLAN and a confident start to high school English"
            ]
          },
          {
            "label": "Audience",
            "name": "Who it's for",
            "heading": "Who it's for",
            "para": "This course is ideal for students who…",
            "bullets": [
              "Are starting high school and want to build strong English habits early",
              "Are sitting Year 7 NAPLAN and want targeted preparation",
              "Aim for stronger performance in class tasks and assessments",
              "Are building writing confidence through regular guided practice",
              "(Parents) want progress to be visible and reported each term"
            ]
          },
          {
            "label": "Benefits",
            "name": "What you'll gain",
            "heading": "What you'll gain",
            "para": "By the end of the term your child will have made measurable progress in:",
            "bullets": [
              "Analytical reading - text analysis, language features, theme and purpose",
              "Extended writing - narrative, persuasive and analytical responses",
              "Grammar and editing - complex sentence structure, paragraphing and revision",
              "NAPLAN and assessment readiness - extended writing under timed conditions"
            ]
          },
          {
            "label": "Content",
            "name": "What's covered",
            "heading": "What's covered",
            "para": "Topics are sequenced across the four school terms:",
            "bullets": [
              "Term 1 - Analytical reading: novels, short stories, language analysis",
              "Term 2 - Narrative and descriptive writing: voice, structure and craft",
              "Term 3 - Persuasive writing and Year 7 NAPLAN preparation",
              "Term 4 - Analytical responses: text response, essay structure and editing"
            ]
          },
          {
            "label": "Program",
            "name": "How it works",
            "heading": "How it works",
            "para": "Every session follows a consistent structure so students always know what to expect:",
            "bullets": [
              "Free level assessment before the first session - no wasted lessons on work already mastered",
              "Each {MINUTES}-min lesson: introduce the concept → model examples → guided practice → independent application",
              "Written and verbal feedback on every student's work each session",
              "End-of-term progress report so parents can see clearly what's improved"
            ]
          }
        ]
      },
      {
        "id": "holiday",
        "icon": "Sun",
        "name": "Holiday intensive",
        "sub": "4 daily lessons in the holidays",
        "items": [
          {
            "label": "Overview",
            "name": "Course overview",
            "heading": "Holiday intensive overview",
            "para": "The Year 7 English holiday intensive runs 4 daily sessions over the school holidays, {MINUTES} minutes each. The program focuses on one writing or analysis genre intensively, building skill and confidence quickly.",
            "bullets": [
              "4 sessions, daily, {MINUTES} minutes each",
              "One genre - deep focus rather than broad coverage",
              "Same small groups and qualified educators as the term course",
              "No prior Schoolola enrolment needed"
            ]
          },
          {
            "label": "Audience",
            "name": "Who it's for",
            "heading": "Who it's for",
            "para": "The holiday intensive suits students who…",
            "bullets": [
              "Are preparing for NAPLAN or upcoming assessments",
              "Want a focused short boost before the next school term begins",
              "Can't commit to a full term but want structured, quality tuition",
              "Need to build confidence in one specific writing or analysis style quickly"
            ]
          },
          {
            "label": "Benefits",
            "name": "What you'll gain",
            "heading": "What you'll gain",
            "para": "After 4 sessions your child will have:",
            "bullets": [
              "A solid understanding of one genre - structure, language features, planning approach",
              "Writing strategies for NAPLAN and high school assessments under timed conditions",
              "Increased confidence to tackle writing and analysis tasks at school",
              "Specific written feedback on their own practice writing"
            ]
          },
          {
            "label": "Content",
            "name": "What's covered",
            "heading": "What's covered",
            "para": "The four sessions follow a structured daily sequence:",
            "bullets": [
              "Day 1 - Genre overview: model texts, key features, planning framework",
              "Day 2 - Planning and drafting: scaffold practice, vocabulary development",
              "Day 3 - Revision and editing: sentence variety, language choices, checking against criteria",
              "Day 4 - Practice task under assessment conditions with full written feedback"
            ]
          },
          {
            "label": "Program",
            "name": "How it works",
            "heading": "How it works",
            "para": "The intensive is designed for maximum impact across four consecutive sessions:",
            "bullets": [
              "Register your interest - sessions fill quickly in school holiday periods",
              "Each day builds on the previous - daily attendance is expected",
              "Individual written feedback on Day 3 and Day 4 tasks included",
              "Students leave with a completed writing portfolio piece"
            ]
          }
        ]
      }
    ],
    "inclusions": [
      {
        "icon": "Award",
        "name": "Qualified educators",
        "desc": "Teachers with {CURRICULUM} expertise and classroom experience."
      },
      {
        "icon": "Users",
        "name": "Small group classes",
        "desc": "8-12 students per class. Your child gets seen, heard and responded to every lesson."
      },
      {
        "icon": "GraduationCap",
        "name": "{CURRICULUM} aligned",
        "desc": "Content mapped directly to {CURRICULUM} outcomes. Complements school, session by session."
      },
      {
        "icon": "MessageCircle",
        "name": "Feedback every lesson",
        "desc": "Structured written and verbal feedback on every student's work, every session."
      },
      {
        "icon": "FileText",
        "name": "End-of-term report",
        "desc": "A clear parent-facing progress summary at end of each term - before school reports arrive."
      },
      {
        "icon": "Target",
        "name": "Free assessment",
        "desc": "Level placement before the first session. No wasted lessons on work already mastered."
      },
      {
        "icon": "CheckCircle2",
        "name": "NAPLAN & high school ready",
        "desc": "Year 7 content directly supports Year 7 NAPLAN and builds the analytical habits required across high school English."
      },
      {
        "icon": "Smartphone",
        "name": "Schoolola LMS",
        "desc": "Access theory lesson videos, online practice questions and track progress easily via the Schoolola LMS app.",
        "images": [
          "/lovable-uploads/lms-parent-dashboard.png",
          "/lovable-uploads/lms-learning-dashboard.png",
          "/lovable-uploads/lms-diagnostic-overview.png",
          "/lovable-uploads/lms-quiz-question-1.png",
          "/lovable-uploads/lms-quiz-question-2.png",
          "/lovable-uploads/lms-results-report.png"
        ]
      }
    ],
    "faq": [
      {
        "q": "Is {PRICE} the full price - are there any extras?",
        "a": "{PRICE} covers the session. Term enrolments include the end-of-term progress report at no extra cost. {BOOKS}"
      },
      {
        "q": "What study materials are provided?",
        "a": "Study materials are provided as bound books - no loose papers. {BOOKS}"
      },
      {
        "q": "How is this different from what happens at school?",
        "a": "Group sizes are smaller (8-12 students per class), lessons follow a consistent structured format, and every student receives written feedback on their own work each session. Content follows the same {CURRICULUM} outcomes - we're giving your child more practice and more attention within the same curriculum."
      },
      {
        "q": "What if my child misses a session?",
        "a": "We ask for consistent attendance as each session builds on the last. If a session is missed, your educator can note the key material to follow up at home - but there are no standard make-up classes. Contact your local centre."
      },
      {
        "q": "Can a confident Year 7 student still be challenged?",
        "a": "Yes. The assessment places students within ability groups, not just year groups. A Year 7 student working above level will be placed in a group that challenges them accordingly."
      }
    ],
    "siblingDesc": "Algebra, geometry and reasoning",
    "naplanRelated": false,
    "seoDescription": "Year 7 English tutoring in {STATE}. {CURRICULUM} aligned analytical reading, extended writing and grammar with Year 7 NAPLAN readiness. Small groups of 8-12 students.",
    "schemaDescription": "Year 7 English tuition in {STATE}. Small groups of 8-12 students, aligned to the {CURRICULUM}. Analytical reading, extended writing, grammar and Year 7 NAPLAN readiness.",
    "quickFacts": [
      {
        "icon": "Clock",
        "text": "60 min / session"
      },
      {
        "icon": "Users",
        "text": "8-12 students"
      },
      {
        "icon": "MapPin",
        "text": "In-centre + online"
      },
      {
        "icon": "DollarSign",
        "text": "{PRICE} / session"
      }
    ]
  },
  "7-maths": {
    "subtitle": "Master algebra, fractions, geometry and reasoning in small groups - aligned to the {CURRICULUM} for Mathematics, with Year 7 NAPLAN preparation built in.",
    "tabs": [
      {
        "id": "term",
        "icon": "Calendar",
        "name": "Term course",
        "sub": "10 weekly sessions per term",
        "items": [
          {
            "label": "Overview",
            "name": "Course overview",
            "heading": "Term Course overview",
            "para": "The Year 7 Maths term course runs 10 sessions per term, {MINUTES} minutes each, in small groups of 8-12 students. Lessons are aligned to the {CURRICULUM} for Mathematics and build the algebraic and reasoning habits expected in high school.",
            "bullets": [
              "Number and algebra, measurement and geometry, and statistics and probability covered across the term",
              "Content sequenced alongside the school year so tutoring and classroom work reinforce each other",
              "Strong preparation for Year 7 NAPLAN numeracy and a confident start to high school maths"
            ]
          },
          {
            "label": "Audience",
            "name": "Who it's for",
            "heading": "Who it's for",
            "para": "This course is ideal for students who…",
            "bullets": [
              "Are starting high school and want a strong foundation in algebra",
              "Are sitting Year 7 NAPLAN and want targeted numeracy preparation",
              "Aim for stronger performance in class tasks and assessments",
              "Are building problem-solving confidence through regular guided practice",
              "(Parents) want progress to be visible and reported each term"
            ]
          },
          {
            "label": "Benefits",
            "name": "What you'll gain",
            "heading": "What you'll gain",
            "para": "By the end of the term your child will have made measurable progress in:",
            "bullets": [
              "Number - integers, fractions, decimals, percentages, ratio and rates",
              "Algebra - variables, expressions, linear equations and substitution",
              "Measurement and geometry - area, volume, angles, triangles and quadrilaterals",
              "NAPLAN and reasoning - multi-step problem solving under timed conditions"
            ]
          },
          {
            "label": "Content",
            "name": "What's covered",
            "heading": "What's covered",
            "para": "Topics are sequenced across the four school terms:",
            "bullets": [
              "Term 1 - Number foundations: integers, fractions, decimals and percentages",
              "Term 2 - Introduction to algebra: expressions, substitution and linear equations",
              "Term 3 - Measurement, geometry and Year 7 NAPLAN numeracy preparation",
              "Term 4 - Statistics, probability and algebraic problem solving"
            ]
          },
          {
            "label": "Program",
            "name": "How it works",
            "heading": "How it works",
            "para": "Every session follows a consistent structure so students always know what to expect:",
            "bullets": [
              "Free level assessment before the first session - no wasted lessons on work already mastered",
              "Each {MINUTES}-min lesson: introduce the concept → worked examples → guided practice → independent problem solving",
              "Written and verbal feedback on every student's work each session",
              "End-of-term progress report so parents can see clearly what's improved"
            ]
          }
        ]
      },
      {
        "id": "holiday",
        "icon": "Sun",
        "name": "Holiday intensive",
        "sub": "4 daily lessons in the holidays",
        "items": [
          {
            "label": "Overview",
            "name": "Course overview",
            "heading": "Holiday intensive overview",
            "para": "The Year 7 Maths holiday intensive runs 4 daily sessions over the school holidays, {MINUTES} minutes each. The program focuses on one key strand intensively, allowing students to build skill and confidence quickly.",
            "bullets": [
              "4 sessions, daily, {MINUTES} minutes each",
              "One maths strand - deep focus rather than broad coverage",
              "Same small groups and qualified educators as the term course",
              "No prior Schoolola enrolment needed"
            ]
          },
          {
            "label": "Audience",
            "name": "Who it's for",
            "heading": "Who it's for",
            "para": "The holiday intensive suits students who…",
            "bullets": [
              "Are preparing for NAPLAN or upcoming assessments",
              "Want a focused short boost before the next school term begins",
              "Can't commit to a full term but want structured, quality tuition",
              "Need to build confidence in one specific maths topic quickly"
            ]
          },
          {
            "label": "Benefits",
            "name": "What you'll gain",
            "heading": "What you'll gain",
            "para": "After 4 sessions your child will have:",
            "bullets": [
              "A solid understanding of one strand - concepts, strategies and worked examples",
              "Numeracy strategies for NAPLAN and high school assessments under timed conditions",
              "Increased confidence to tackle maths tasks at school",
              "Specific written feedback on their own practice problems"
            ]
          },
          {
            "label": "Content",
            "name": "What's covered",
            "heading": "What's covered",
            "para": "The four sessions follow a structured daily sequence:",
            "bullets": [
              "Day 1 - Strand overview: key concepts, worked examples, mental strategies",
              "Day 2 - Guided practice: scaffold problems, vocabulary and notation",
              "Day 3 - Problem solving: multi-step questions, reasoning and checking",
              "Day 4 - Practice task under assessment conditions with full written feedback"
            ]
          },
          {
            "label": "Program",
            "name": "How it works",
            "heading": "How it works",
            "para": "The intensive is designed for maximum impact across four consecutive sessions:",
            "bullets": [
              "Register your interest - sessions fill quickly in school holiday periods",
              "Each day builds on the previous - daily attendance is expected",
              "Individual written feedback on Day 3 and Day 4 tasks included",
              "Students leave with a completed problem-solving portfolio"
            ]
          }
        ]
      }
    ],
    "inclusions": [
      {
        "icon": "Award",
        "name": "Qualified educators",
        "desc": "Teachers with {CURRICULUM} expertise and classroom experience."
      },
      {
        "icon": "Users",
        "name": "Small group classes",
        "desc": "8-12 students per class. Your child gets seen, heard and responded to every lesson."
      },
      {
        "icon": "GraduationCap",
        "name": "{CURRICULUM} aligned",
        "desc": "Content mapped directly to {CURRICULUM} outcomes. Complements school, session by session."
      },
      {
        "icon": "MessageCircle",
        "name": "Feedback every lesson",
        "desc": "Structured written and verbal feedback on every student's work, every session."
      },
      {
        "icon": "FileText",
        "name": "End-of-term report",
        "desc": "A clear parent-facing progress summary at end of each term - before school reports arrive."
      },
      {
        "icon": "Target",
        "name": "Free assessment",
        "desc": "Level placement before the first session. No wasted lessons on work already mastered."
      },
      {
        "icon": "CheckCircle2",
        "name": "NAPLAN & high school ready",
        "desc": "Year 7 content directly supports Year 7 NAPLAN numeracy and builds the algebraic habits required across Years 7-8 maths."
      },
      {
        "icon": "Smartphone",
        "name": "Schoolola LMS",
        "desc": "Access theory lesson videos, online practice questions and track progress easily via the Schoolola LMS app.",
        "images": [
          "/lovable-uploads/lms-parent-dashboard.png",
          "/lovable-uploads/lms-learning-dashboard.png",
          "/lovable-uploads/lms-diagnostic-overview.png",
          "/lovable-uploads/lms-quiz-question-1.png",
          "/lovable-uploads/lms-quiz-question-2.png",
          "/lovable-uploads/lms-results-report.png"
        ]
      }
    ],
    "faq": [
      {
        "q": "Is {PRICE} the full price - are there any extras?",
        "a": "{PRICE} covers the session. Term enrolments include the end-of-term progress report at no extra cost. {BOOKS}"
      },
      {
        "q": "What study materials are provided?",
        "a": "Study materials are provided as bound books - no loose papers. {BOOKS}"
      },
      {
        "q": "How is this different from what happens at school?",
        "a": "Group sizes are smaller (8-12 students per class), lessons follow a consistent structured format, and every student receives written feedback on their own work each session. Content follows the same {CURRICULUM} outcomes - we're giving your child more practice and more attention within the same curriculum."
      },
      {
        "q": "What if my child misses a session?",
        "a": "We ask for consistent attendance as each session builds on the last. If a session is missed, your educator can note the key material to follow up at home - but there are no standard make-up classes. Contact your local centre."
      },
      {
        "q": "Can a confident Year 7 student still be challenged?",
        "a": "Yes. The assessment places students within ability groups, not just year groups. A Year 7 student working above level will be placed in a group that challenges them accordingly."
      }
    ],
    "siblingDesc": "Analytical reading, writing and grammar",
    "naplanRelated": false,
    "seoDescription": "Year 7 Maths tutoring in {STATE}. {CURRICULUM} aligned algebra, fractions, geometry and reasoning with Year 7 NAPLAN readiness. Small groups of 8-12 students.",
    "schemaDescription": "Year 7 Maths tuition in {STATE}. Small groups of 8-12 students, aligned to the {CURRICULUM}. Algebra, fractions, geometry, reasoning and Year 7 NAPLAN readiness.",
    "quickFacts": [
      {
        "icon": "Clock",
        "text": "60 min / session"
      },
      {
        "icon": "Users",
        "text": "8-12 students"
      },
      {
        "icon": "MapPin",
        "text": "In-centre + online"
      },
      {
        "icon": "DollarSign",
        "text": "{PRICE} / session"
      }
    ]
  },
  "8-english": {
    "subtitle": "Deepen analytical reading, extended writing and language conventions in small groups - aligned to the {CURRICULUM} for English and a strong bridge into senior English.",
    "tabs": [
      {
        "id": "term",
        "icon": "Calendar",
        "name": "Term course",
        "sub": "10 weekly sessions per term",
        "items": [
          {
            "label": "Overview",
            "name": "Course overview",
            "heading": "Term Course overview",
            "para": "The Year 8 English term course runs 10 sessions per term, {MINUTES} minutes each, in small groups of 8-12 students. Lessons consolidate {CURRICULUM} outcomes and build the analytical writing habits required in Years 9-10.",
            "bullets": [
              "Analytical reading, extended writing and grammar covered across the term",
              "Content sequenced alongside the school year so tutoring and classroom work reinforce each other",
              "Strong preparation for in-school assessments and Years 9-10 English"
            ]
          },
          {
            "label": "Audience",
            "name": "Who it's for",
            "heading": "Who it's for",
            "para": "This course is ideal for students who…",
            "bullets": [
              "Want to consolidate Years 7-8 English before stepping into Years 9-10",
              "Aim for stronger performance in class tasks and assessments",
              "Are building writing confidence through regular guided practice",
              "(Parents) want progress to be visible and reported each term"
            ]
          },
          {
            "label": "Benefits",
            "name": "What you'll gain",
            "heading": "What you'll gain",
            "para": "By the end of the term your child will have made measurable progress in:",
            "bullets": [
              "Analytical reading - text analysis, language features, theme and context",
              "Extended writing - narrative, persuasive and analytical responses",
              "Grammar and editing - complex sentence structure, paragraphing and revision",
              "Assessment readiness - extended responses and essays under timed conditions"
            ]
          },
          {
            "label": "Content",
            "name": "What's covered",
            "heading": "What's covered",
            "para": "Topics are sequenced across the four school terms:",
            "bullets": [
              "Term 1 - Analytical reading: novels, poetry and language analysis",
              "Term 2 - Narrative and descriptive writing: voice, structure and craft",
              "Term 3 - Persuasive writing",
              "Term 4 - Essay writing: text response, structure and editing for Years 9-10"
            ]
          },
          {
            "label": "Program",
            "name": "How it works",
            "heading": "How it works",
            "para": "Every session follows a consistent structure so students always know what to expect:",
            "bullets": [
              "Free level assessment before the first session - no wasted lessons on work already mastered",
              "Each {MINUTES}-min lesson: introduce the concept → model examples → guided practice → independent application",
              "Written and verbal feedback on every student's work each session",
              "End-of-term progress report so parents can see clearly what's improved"
            ]
          }
        ]
      },
      {
        "id": "holiday",
        "icon": "Sun",
        "name": "Holiday intensive",
        "sub": "4 daily lessons in the holidays",
        "items": [
          {
            "label": "Overview",
            "name": "Course overview",
            "heading": "Holiday intensive overview",
            "para": "The Year 8 English holiday intensive runs 4 daily sessions over the school holidays, {MINUTES} minutes each. The program focuses on one writing or analysis genre intensively, building skill and confidence quickly.",
            "bullets": [
              "4 sessions, daily, {MINUTES} minutes each",
              "One genre - deep focus rather than broad coverage",
              "Same small groups and qualified educators as the term course",
              "No prior Schoolola enrolment needed"
            ]
          },
          {
            "label": "Audience",
            "name": "Who it's for",
            "heading": "Who it's for",
            "para": "The holiday intensive suits students who…",
            "bullets": [
              "Are preparing for upcoming assessments",
              "Want a focused short boost before the next school term begins",
              "Can't commit to a full term but want structured, quality tuition",
              "Need to build confidence in one specific writing or analysis style quickly"
            ]
          },
          {
            "label": "Benefits",
            "name": "What you'll gain",
            "heading": "What you'll gain",
            "para": "After 4 sessions your child will have:",
            "bullets": [
              "A solid understanding of one genre - structure, language features, planning approach",
              "Writing strategies for assessments under timed conditions",
              "Increased confidence to tackle writing and analysis tasks at school",
              "Specific written feedback on their own practice writing"
            ]
          },
          {
            "label": "Content",
            "name": "What's covered",
            "heading": "What's covered",
            "para": "The four sessions follow a structured daily sequence:",
            "bullets": [
              "Day 1 - Genre overview: model texts, key features, planning framework",
              "Day 2 - Planning and drafting: scaffold practice, vocabulary development",
              "Day 3 - Revision and editing: sentence variety, language choices, checking against criteria",
              "Day 4 - Practice task under assessment conditions with full written feedback"
            ]
          },
          {
            "label": "Program",
            "name": "How it works",
            "heading": "How it works",
            "para": "The intensive is designed for maximum impact across four consecutive sessions:",
            "bullets": [
              "Register your interest - sessions fill quickly in school holiday periods",
              "Each day builds on the previous - daily attendance is expected",
              "Individual written feedback on Day 3 and Day 4 tasks included",
              "Students leave with a completed writing portfolio piece"
            ]
          }
        ]
      }
    ],
    "inclusions": [
      {
        "icon": "Award",
        "name": "Qualified educators",
        "desc": "Teachers with {CURRICULUM} expertise and classroom experience."
      },
      {
        "icon": "Users",
        "name": "Small group classes",
        "desc": "8-12 students per class. Your child gets seen, heard and responded to every lesson."
      },
      {
        "icon": "GraduationCap",
        "name": "{CURRICULUM} aligned",
        "desc": "Content mapped directly to {CURRICULUM} outcomes. Complements school, session by session."
      },
      {
        "icon": "MessageCircle",
        "name": "Feedback every lesson",
        "desc": "Structured written and verbal feedback on every student's work, every session."
      },
      {
        "icon": "FileText",
        "name": "End-of-term report",
        "desc": "A clear parent-facing progress summary at end of each term - before school reports arrive."
      },
      {
        "icon": "Target",
        "name": "Free assessment",
        "desc": "Level placement before the first session. No wasted lessons on work already mastered."
      },
      {
        "icon": "CheckCircle2",
        "name": "Years 9-10 ready",
        "desc": "Year 8 content builds the analytical habits required across Years 9-10 English."
      },
      {
        "icon": "Smartphone",
        "name": "Schoolola LMS",
        "desc": "Access theory lesson videos, online practice questions and track progress easily via the Schoolola LMS app.",
        "images": [
          "/lovable-uploads/lms-parent-dashboard.png",
          "/lovable-uploads/lms-learning-dashboard.png",
          "/lovable-uploads/lms-diagnostic-overview.png",
          "/lovable-uploads/lms-quiz-question-1.png",
          "/lovable-uploads/lms-quiz-question-2.png",
          "/lovable-uploads/lms-results-report.png"
        ]
      }
    ],
    "faq": [
      {
        "q": "Is {PRICE} the full price - are there any extras?",
        "a": "{PRICE} covers the session. Term enrolments include the end-of-term progress report at no extra cost. {BOOKS}"
      },
      {
        "q": "What study materials are provided?",
        "a": "Study materials are provided as bound books - no loose papers. {BOOKS}"
      },
      {
        "q": "How is this different from what happens at school?",
        "a": "Group sizes are smaller (8-12 students per class), lessons follow a consistent structured format, and every student receives written feedback on their own work each session. Content follows the same {CURRICULUM} outcomes - we're giving your child more practice and more attention within the same curriculum."
      },
      {
        "q": "What if my child misses a session?",
        "a": "We ask for consistent attendance as each session builds on the last. If a session is missed, your educator can note the key material to follow up at home - but there are no standard make-up classes. Contact your local centre."
      },
      {
        "q": "Can a confident Year 8 student still be challenged?",
        "a": "Yes. The assessment places students within ability groups, not just year groups. A Year 8 student working above level will be placed in a group that challenges them accordingly."
      }
    ],
    "siblingDesc": "Algebra, geometry and reasoning",
    "naplanRelated": false,
    "seoDescription": "Year 8 English tutoring in {STATE}. {CURRICULUM} aligned analytical reading, extended writing and grammar with Years 9-10 readiness. Small groups of 8-12 students.",
    "schemaDescription": "Year 8 English tuition in {STATE}. Small groups of 8-12 students, aligned to the {CURRICULUM}. Analytical reading, extended writing, grammar and Years 9-10 readiness.",
    "quickFacts": [
      {
        "icon": "Clock",
        "text": "60 min / session"
      },
      {
        "icon": "Users",
        "text": "8-12 students"
      },
      {
        "icon": "MapPin",
        "text": "In-centre + online"
      },
      {
        "icon": "DollarSign",
        "text": "{PRICE} / session"
      }
    ]
  },
  "8-maths": {
    "subtitle": "Strengthen algebra, geometry, indices and reasoning in small groups - aligned to the {CURRICULUM} for Mathematics and a strong bridge into Years 9-10 maths.",
    "tabs": [
      {
        "id": "term",
        "icon": "Calendar",
        "name": "Term course",
        "sub": "10 weekly sessions per term",
        "items": [
          {
            "label": "Overview",
            "name": "Course overview",
            "heading": "Term Course overview",
            "para": "The Year 8 Maths term course runs 10 sessions per term, {MINUTES} minutes each, in small groups of 8-12 students. Lessons consolidate {CURRICULUM} outcomes and build the algebraic and reasoning habits required in Years 9-10.",
            "bullets": [
              "Number and algebra, measurement and geometry, and statistics and probability covered across the term",
              "Content sequenced alongside the school year so tutoring and classroom work reinforce each other",
              "Strong preparation for in-school assessments and Years 9-10 maths"
            ]
          },
          {
            "label": "Audience",
            "name": "Who it's for",
            "heading": "Who it's for",
            "para": "This course is ideal for students who…",
            "bullets": [
              "Want to consolidate Years 7-8 algebra before stepping into Years 9-10",
              "Aim for stronger performance in class tasks and assessments",
              "Are building problem-solving confidence through regular guided practice",
              "(Parents) want progress to be visible and reported each term"
            ]
          },
          {
            "label": "Benefits",
            "name": "What you'll gain",
            "heading": "What you'll gain",
            "para": "By the end of the term your child will have made measurable progress in:",
            "bullets": [
              "Number - integers, fractions, decimals, percentages, ratio, rates and indices",
              "Algebra - expressions, linear equations, inequalities and the Cartesian plane",
              "Measurement and geometry - Pythagoras, area, volume, congruent figures",
              "Statistics, probability and reasoning - multi-step problem solving"
            ]
          },
          {
            "label": "Content",
            "name": "What's covered",
            "heading": "What's covered",
            "para": "Topics are sequenced across the four school terms:",
            "bullets": [
              "Term 1 - Number and indices: fractions, decimals, percentages and index laws",
              "Term 2 - Algebra: expressions, linear equations, inequalities, Cartesian plane",
              "Term 3 - Measurement and geometry: Pythagoras, area, volume, congruence",
              "Term 4 - Statistics, probability and algebraic problem solving for Years 9-10"
            ]
          },
          {
            "label": "Program",
            "name": "How it works",
            "heading": "How it works",
            "para": "Every session follows a consistent structure so students always know what to expect:",
            "bullets": [
              "Free level assessment before the first session - no wasted lessons on work already mastered",
              "Each {MINUTES}-min lesson: introduce the concept → worked examples → guided practice → independent problem solving",
              "Written and verbal feedback on every student's work each session",
              "End-of-term progress report so parents can see clearly what's improved"
            ]
          }
        ]
      },
      {
        "id": "holiday",
        "icon": "Sun",
        "name": "Holiday intensive",
        "sub": "4 daily lessons in the holidays",
        "items": [
          {
            "label": "Overview",
            "name": "Course overview",
            "heading": "Holiday intensive overview",
            "para": "The Year 8 Maths holiday intensive runs 4 daily sessions over the school holidays, {MINUTES} minutes each. The program focuses on one key strand intensively, allowing students to build skill and confidence quickly.",
            "bullets": [
              "4 sessions, daily, {MINUTES} minutes each",
              "One maths strand - deep focus rather than broad coverage",
              "Same small groups and qualified educators as the term course",
              "No prior Schoolola enrolment needed"
            ]
          },
          {
            "label": "Audience",
            "name": "Who it's for",
            "heading": "Who it's for",
            "para": "The holiday intensive suits students who…",
            "bullets": [
              "Are preparing for upcoming assessments",
              "Want a focused short boost before the next school term begins",
              "Can't commit to a full term but want structured, quality tuition",
              "Need to build confidence in one specific maths topic quickly"
            ]
          },
          {
            "label": "Benefits",
            "name": "What you'll gain",
            "heading": "What you'll gain",
            "para": "After 4 sessions your child will have:",
            "bullets": [
              "A solid understanding of one strand - concepts, strategies and worked examples",
              "Numeracy strategies for assessments under timed conditions",
              "Increased confidence to tackle maths tasks at school",
              "Specific written feedback on their own practice problems"
            ]
          },
          {
            "label": "Content",
            "name": "What's covered",
            "heading": "What's covered",
            "para": "The four sessions follow a structured daily sequence:",
            "bullets": [
              "Day 1 - Strand overview: key concepts, worked examples, mental strategies",
              "Day 2 - Guided practice: scaffold problems, vocabulary and notation",
              "Day 3 - Problem solving: multi-step questions, reasoning and checking",
              "Day 4 - Practice task under assessment conditions with full written feedback"
            ]
          },
          {
            "label": "Program",
            "name": "How it works",
            "heading": "How it works",
            "para": "The intensive is designed for maximum impact across four consecutive sessions:",
            "bullets": [
              "Register your interest - sessions fill quickly in school holiday periods",
              "Each day builds on the previous - daily attendance is expected",
              "Individual written feedback on Day 3 and Day 4 tasks included",
              "Students leave with a completed problem-solving portfolio"
            ]
          }
        ]
      }
    ],
    "inclusions": [
      {
        "icon": "Award",
        "name": "Qualified educators",
        "desc": "Teachers with {CURRICULUM} expertise and classroom experience."
      },
      {
        "icon": "Users",
        "name": "Small group classes",
        "desc": "8-12 students per class. Your child gets seen, heard and responded to every lesson."
      },
      {
        "icon": "GraduationCap",
        "name": "{CURRICULUM} aligned",
        "desc": "Content mapped directly to {CURRICULUM} outcomes. Complements school, session by session."
      },
      {
        "icon": "MessageCircle",
        "name": "Feedback every lesson",
        "desc": "Structured written and verbal feedback on every student's work, every session."
      },
      {
        "icon": "FileText",
        "name": "End-of-term report",
        "desc": "A clear parent-facing progress summary at end of each term - before school reports arrive."
      },
      {
        "icon": "Target",
        "name": "Free assessment",
        "desc": "Level placement before the first session. No wasted lessons on work already mastered."
      },
      {
        "icon": "CheckCircle2",
        "name": "Years 9-10 ready",
        "desc": "Year 8 content builds the algebraic and reasoning habits required across Years 9-10 maths."
      },
      {
        "icon": "Smartphone",
        "name": "Schoolola LMS",
        "desc": "Access theory lesson videos, online practice questions and track progress easily via the Schoolola LMS app.",
        "images": [
          "/lovable-uploads/lms-parent-dashboard.png",
          "/lovable-uploads/lms-learning-dashboard.png",
          "/lovable-uploads/lms-diagnostic-overview.png",
          "/lovable-uploads/lms-quiz-question-1.png",
          "/lovable-uploads/lms-quiz-question-2.png",
          "/lovable-uploads/lms-results-report.png"
        ]
      }
    ],
    "faq": [
      {
        "q": "Is {PRICE} the full price - are there any extras?",
        "a": "{PRICE} covers the session. Term enrolments include the end-of-term progress report at no extra cost. {BOOKS}"
      },
      {
        "q": "What study materials are provided?",
        "a": "Study materials are provided as bound books - no loose papers. {BOOKS}"
      },
      {
        "q": "How is this different from what happens at school?",
        "a": "Group sizes are smaller (8-12 students per class), lessons follow a consistent structured format, and every student receives written feedback on their own work each session. Content follows the same {CURRICULUM} outcomes - we're giving your child more practice and more attention within the same curriculum."
      },
      {
        "q": "What if my child misses a session?",
        "a": "We ask for consistent attendance as each session builds on the last. If a session is missed, your educator can note the key material to follow up at home - but there are no standard make-up classes. Contact your local centre."
      },
      {
        "q": "Can a confident Year 8 student still be challenged?",
        "a": "Yes. The assessment places students within ability groups, not just year groups. A Year 8 student working above level will be placed in a group that challenges them accordingly."
      }
    ],
    "siblingDesc": "Analytical reading, writing and grammar",
    "naplanRelated": false,
    "seoDescription": "Year 8 Maths tutoring in {STATE}. {CURRICULUM} aligned algebra, indices, geometry and reasoning with Years 9-10 readiness. Small groups of 8-12 students.",
    "schemaDescription": "Year 8 Maths tuition in {STATE}. Small groups of 8-12 students, aligned to the {CURRICULUM}. Algebra, indices, geometry, reasoning and Years 9-10 readiness.",
    "quickFacts": [
      {
        "icon": "Clock",
        "text": "60 min / session"
      },
      {
        "icon": "Users",
        "text": "8-12 students"
      },
      {
        "icon": "MapPin",
        "text": "In-centre + online"
      },
      {
        "icon": "DollarSign",
        "text": "{PRICE} / session"
      }
    ]
  }
};
